'use client';

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  MenuItem,
  Slider,
  TextField,
  Paper,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  ColumnDirective,
  ColumnsDirective,
  Filter,
  GridComponent,
  Inject,
  Page,
  Resize,
  Sort,
} from '@syncfusion/ej2-react-grids';
import { L10n, setCulture } from '@syncfusion/ej2-base';
import { signIn, useSession } from 'next-auth/react';
import { Info, FolderOpen, Save, Share2 } from 'lucide-react';
import { InstrumentTypeOption, InstrumentRow } from '@/components/catalogo/types';
import { buildCatalogoBaseQueryString } from '@/components/catalogo/catalogo-query-filters';
import { AppLink } from '@/components/mui/app-link';
import {
  CATALOGO_ALL_COLUMN_FIELDS,
  buildCatalogoCompactQueryParams,
  decodeCatalogoCompactFromParams,
} from '@/libs/catalogo-compact-state';
import { parseOptionalNumber } from '@/libs/fund-data-utils';
import { useAppColors } from '@/styles/colors';
import { displayInstrument } from '@/libs/normalize-ticker';

L10n.load({
  pt: {
    grid: {
      EmptyRecord: 'Nenhum registro encontrado',
      GroupDropArea: 'Arraste um cabeçalho para agrupar pela coluna',
      UnGroup: 'Clique aqui para desagrupar',
      Item: 'item',
      Items: 'itens',
      Search: 'Buscar',
      FilterButton: 'Filtrar',
      ClearButton: 'Limpar',
      StartsWith: 'Começa com',
      EndsWith: 'Termina com',
      Contains: 'Contém',
      Equal: 'Igual',
      NotEqual: 'Diferente',
      GreaterThan: 'Maior que',
      LessThan: 'Menor que',
      GreaterThanOrEqual: 'Maior ou igual',
      LessThanOrEqual: 'Menor ou igual',
      SortAscending: 'Ordenar A-Z',
      SortDescending: 'Ordenar Z-A',
      FirstPage: 'Primeira página',
      LastPage: 'Última página',
      PreviousPage: 'Página anterior',
      NextPage: 'Próxima página',
      Page: 'Página',
      Of: 'de',
      ItemsPerPage: 'Linhas por página',
      itemsPerPage: 'Linhas por página',
      PagerDropDown: 'Linhas por página',
      PagerAllDropDown: 'Linhas por página',
      TotalItemsInfo: '({0} itens)',
    },
    pager: {
      currentPageInfo: '{0} de {1} páginas',
      totalItemsInfo: '({0} itens)',
      itemsPerPage: 'Linhas por página',
      ItemsPerPage: 'Linhas por página',
      pagerDropDown: 'Linhas por página',
      PagerDropDown: 'Linhas por página',
      pagerAllDropDown: 'Linhas por página',
      perPage: 'Linhas por página',
      perPageText: 'Linhas por página',
      firstPageTooltip: 'Primeira página',
      lastPageTooltip: 'Última página',
      nextPageTooltip: 'Próxima página',
      previousPageTooltip: 'Página anterior',
    },
  },
  'pt-BR': {
    grid: {
      EmptyRecord: 'Nenhum registro encontrado',
      GroupDropArea: 'Arraste um cabeçalho para agrupar pela coluna',
      UnGroup: 'Clique aqui para desagrupar',
      Item: 'item',
      Items: 'itens',
      Search: 'Buscar',
      FilterButton: 'Filtrar',
      ClearButton: 'Limpar',
      StartsWith: 'Começa com',
      EndsWith: 'Termina com',
      Contains: 'Contém',
      Equal: 'Igual',
      NotEqual: 'Diferente',
      GreaterThan: 'Maior que',
      LessThan: 'Menor que',
      GreaterThanOrEqual: 'Maior ou igual',
      LessThanOrEqual: 'Menor ou igual',
      SortAscending: 'Ordenar A-Z',
      SortDescending: 'Ordenar Z-A',
      FirstPage: 'Primeira página',
      LastPage: 'Última página',
      PreviousPage: 'Página anterior',
      NextPage: 'Próxima página',
      Page: 'Página',
      Of: 'de',
      ItemsPerPage: 'Linhas por página',
      itemsPerPage: 'Linhas por página',
      PagerDropDown: 'Linhas por página',
      PagerAllDropDown: 'Linhas por página',
      TotalItemsInfo: '({0} itens)',
    },
    pager: {
      currentPageInfo: '{0} de {1} páginas',
      totalItemsInfo: '({0} itens)',
      itemsPerPage: 'Linhas por página',
      ItemsPerPage: 'Linhas por página',
      pagerDropDown: 'Linhas por página',
      PagerDropDown: 'Linhas por página',
      pagerAllDropDown: 'Linhas por página',
      perPage: 'Linhas por página',
      perPageText: 'Linhas por página',
      firstPageTooltip: 'Primeira página',
      lastPageTooltip: 'Última página',
      nextPageTooltip: 'Próxima página',
      previousPageTooltip: 'Página anterior',
    },
  },
});
if (typeof window !== 'undefined') {
  setCulture('pt');
}

const COLUMN_CHOOSER_FULL_LABELS: Record<string, string> = {
  fundTypeSymbol: 'Tipo',
  idCem: 'Ticker',
  name: 'Nome',
  currentPvp: 'P/VP atual',
  dy12mPct: 'DY 12m',
  numeroCotistas: 'Número de cotistas',
  vacanciaPct: 'Vacância',
  inadimplenciaPct: 'Inadimplência',
  volatilityAnnualized21dPct: 'Volatilidade 21d a.a.',
  cashPressureMonths12m: 'Meses sob pressão (12m)',
  cashTrendStatus: 'Status do caixa',
  distanceTo12mHighPct: 'Dist. da máxima 12m',
  concentracaoTijoloPct: 'Concentração Tijolo',
  concentracaoPapelPct: 'Concentração Papel',
  liquidezDiaria30d: 'Liquidez diária (30d)',
  selicSensitivityScore: 'Sensibilidade à Selic',
  leverageIndexScore: 'Índice de alavancagem',
  vpPerShareGrowth12mPct: 'Crescimento VP/cota (12m)',
  patrimonioLiquido: 'Patrimônio',
  classificacao: 'Classificação',
  segmento: 'Segmento',
};

type RangeOptions = {
  clampMin?: number;
  clampMax?: number;
  trimPercentile?: number;
};

const computeRange = (
  rows: InstrumentRow[],
  selector: (row: InstrumentRow) => number | null | undefined,
  options: RangeOptions = {},
): { min: number; max: number } | null => {
  const { clampMin, clampMax, trimPercentile = 0 } = options;
  let values = rows
    .map(selector)
    .filter((value): value is number => typeof value === 'number' && Number.isFinite(value));
  if (values.length === 0) return null;
  if (clampMin !== undefined) {
    values = values.filter((value) => value >= clampMin);
  }
  if (clampMax !== undefined) {
    values = values.filter((value) => value <= clampMax);
  }
  if (values.length === 0) return null;
  values.sort((a, b) => a - b);
  const p = Math.min(Math.max(trimPercentile, 0), 0.25);
  const lowIdx = Math.floor((values.length - 1) * p);
  const highIdx = Math.ceil((values.length - 1) * (1 - p));
  const trimmedMin = values[lowIdx];
  const trimmedMax = values[highIdx];
  if (!Number.isFinite(trimmedMin) || !Number.isFinite(trimmedMax) || trimmedMin >= trimmedMax) {
    return {
      min: values[0],
      max: values[values.length - 1],
    };
  }
  return {
    min: trimmedMin,
    max: trimmedMax,
  };
};

type RangeSliderScale = 'linear' | 'logarithmic';

const LOGARITHMIC_SLIDER_STEPS = 1000;

const toSliderCoordinate = (value: number, bounds: { min: number; max: number }, scale: RangeSliderScale) => {
  if (scale === 'linear') return value;

  const min = Math.max(bounds.min, 0);
  const max = Math.max(bounds.max, min);
  if (max <= min) return 0;

  const ratio =
    min > 0
      ? Math.log(Math.max(value, min) / min) / Math.log(max / min)
      : Math.log1p(Math.max(value, 0)) / Math.log1p(max);
  return Math.min(Math.max(ratio, 0), 1) * LOGARITHMIC_SLIDER_STEPS;
};

const fromSliderCoordinate = (coordinate: number, bounds: { min: number; max: number }, scale: RangeSliderScale) => {
  if (scale === 'linear') return coordinate;

  const min = Math.max(bounds.min, 0);
  const max = Math.max(bounds.max, min);
  if (max <= min) return min;

  const ratio = Math.min(Math.max(coordinate / LOGARITHMIC_SLIDER_STEPS, 0), 1);
  const value = min > 0 ? min * Math.exp(ratio * Math.log(max / min)) : Math.expm1(ratio * Math.log1p(max));
  return Math.min(Math.max(value, min), max);
};

type RangeFilterCardProps = {
  label: ReactNode;
  minValue: string;
  maxValue: string;
  onMinChange: (value: string) => void;
  onMaxChange: (value: string) => void;
  suffix?: string;
  sliderBounds?: { min: number; max: number } | null;
  sliderStep?: number;
  sliderScale?: RangeSliderScale;
  isActive?: boolean;
};

const RangeFilterCard = ({
  label,
  minValue,
  maxValue,
  onMinChange,
  onMaxChange,
  suffix,
  sliderBounds,
  sliderStep = 0.01,
  sliderScale = 'linear',
  isActive = false,
}: RangeFilterCardProps) => {
  const appColors = useAppColors();
  const parseSliderInputValue = (value: string) => {
    if (sliderStep >= 1) {
      const normalized = (value ?? '').trim().replace(/[^\d-]/g, '');
      if (!normalized || normalized === '-') return null;
      const parsed = Number(normalized);
      return Number.isFinite(parsed) ? parsed : null;
    }
    return parseOptionalNumber(value);
  };
  const sliderConfig =
    sliderBounds && sliderBounds.max > sliderBounds.min
      ? {
          min: sliderScale === 'logarithmic' ? 0 : sliderBounds.min,
          max: sliderScale === 'logarithmic' ? LOGARITHMIC_SLIDER_STEPS : sliderBounds.max,
          step: sliderScale === 'logarithmic' ? 1 : sliderStep,
        }
      : null;
  const sliderValue =
    sliderBounds && sliderConfig
      ? (() => {
          const minInput = parseSliderInputValue(minValue) ?? sliderBounds.min;
          const maxInput = parseSliderInputValue(maxValue) ?? sliderBounds.max;
          const lowerValue = Math.min(minInput, maxInput);
          const upperValue = Math.max(minInput, maxInput);
          const boundedMin = Math.min(Math.max(lowerValue, sliderBounds.min), sliderBounds.max);
          const boundedMax = Math.max(Math.min(upperValue, sliderBounds.max), sliderBounds.min);
          return [
            toSliderCoordinate(boundedMin, sliderBounds, sliderScale),
            toSliderCoordinate(boundedMax, sliderBounds, sliderScale),
          ];
        })()
      : null;

  return (
    <Paper
      variant='outlined'
      sx={{
        px: 2,
        py: 1.25,
        borderRadius: 2,
        borderColor: isActive ? appColors.semantic.violet : 'hsl(var(--border) / 0.9)',
        backgroundColor: 'hsl(var(--background) / 0.95)',
        boxShadow: '0 1px 3px hsl(var(--foreground) / 0.06)',
      }}
    >
      <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ mb: 0.7 }}>
        <Typography
          variant='caption'
          color='text.secondary'
          sx={{ display: 'block', fontWeight: 700, letterSpacing: 0.2 }}
        >
          {label}
        </Typography>
      </Stack>
      <Stack direction='row' spacing={1}>
        <TextField
          size='small'
          placeholder='Min'
          value={minValue}
          onChange={(event) => onMinChange(event.target.value)}
          fullWidth
          sx={{
            '& .MuiInputBase-root': { height: 34 },
            '& .MuiInputBase-input': { fontSize: 12.5, px: 1.1, py: 0.6 },
            '& .MuiInputAdornment-root': {
              fontSize: 12,
              color: 'text.secondary',
            },
          }}
          InputProps={
            suffix
              ? {
                  endAdornment: <InputAdornment position='end'>{suffix}</InputAdornment>,
                }
              : undefined
          }
        />
        <TextField
          size='small'
          placeholder='Max'
          value={maxValue}
          onChange={(event) => onMaxChange(event.target.value)}
          fullWidth
          sx={{
            '& .MuiInputBase-root': { height: 34 },
            '& .MuiInputBase-input': { fontSize: 12.5, px: 1.1, py: 0.6 },
            '& .MuiInputAdornment-root': {
              fontSize: 12,
              color: 'text.secondary',
            },
          }}
          InputProps={
            suffix
              ? {
                  endAdornment: <InputAdornment position='end'>{suffix}</InputAdornment>,
                }
              : undefined
          }
        />
      </Stack>
      {sliderBounds && sliderConfig && sliderValue ? (
        <Slider
          size='small'
          value={sliderValue}
          min={sliderConfig.min}
          max={sliderConfig.max}
          step={sliderConfig.step}
          onChange={(_, value) => {
            if (!Array.isArray(value)) return;
            const [nextMin, nextMax] = value.map((coordinate) =>
              fromSliderCoordinate(coordinate, sliderBounds, sliderScale),
            );
            if (sliderStep >= 1) {
              onMinChange(INTEGER_INPUT_FORMATTER.format(Math.round(nextMin)));
              onMaxChange(INTEGER_INPUT_FORMATTER.format(Math.round(nextMax)));
              return;
            }
            onMinChange(String(Number(nextMin.toFixed(4))));
            onMaxChange(String(Number(nextMax.toFixed(4))));
          }}
          disableSwap
          sx={{
            mt: 0.8,
            display: 'block',
            width: '100%',
            mx: 0,
            color: appColors.actions.range.selectedBg,
            '& .MuiSlider-rail': {
              height: 4,
              borderRadius: 999,
              backgroundColor: 'hsl(var(--border))',
              opacity: 1,
            },
            '& .MuiSlider-track': {
              height: 4,
              borderRadius: 999,
              border: 'none',
              backgroundColor: appColors.actions.range.selectedBg,
            },
            '& .MuiSlider-thumb': {
              width: 10,
              height: 10,
              backgroundColor: appColors.actions.range.selectedBg,
              border: '2px solid hsl(var(--card))',
              boxShadow: '0 0 0 1px hsl(var(--border))',
              '&:hover, &.Mui-focusVisible, &.Mui-active': {
                boxShadow: '0 0 0 3px hsl(var(--primary) / 0.18)',
              },
            },
          }}
        />
      ) : null}
    </Paper>
  );
};

const FilterLabel = ({ label, help }: { label: string; help: string }) => (
  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
    <span>{label}</span>
    <Tooltip title={help} arrow placement='top'>
      <Box
        component='span'
        sx={{
          width: 14,
          height: 14,
          borderRadius: '50%',
          border: '1px solid',
          borderColor: 'text.secondary',
          color: 'text.secondary',
          fontSize: 10,
          lineHeight: '12px',
          textAlign: 'center',
          display: 'inline-block',
          cursor: 'help',
          fontWeight: 700,
        }}
      >
        i
      </Box>
    </Tooltip>
  </Box>
);

const parseRangeInput = (minInput: string, maxInput: string) => {
  const min = parseOptionalNumber(minInput);
  const max = parseOptionalNumber(maxInput);
  if (min !== null && max !== null && min > max) {
    return { min: max, max: min };
  }
  return { min, max };
};

const parseRangeInputInteger = (minInput: string, maxInput: string) => {
  const parseIntegerLike = (value: string) => {
    const trimmed = (value ?? '').trim();
    if (!trimmed) return null;
    const normalized = trimmed.replace(/[^\d-]/g, '');
    if (!normalized || normalized === '-') return null;
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const min = parseIntegerLike(minInput);
  const max = parseIntegerLike(maxInput);
  if (min !== null && max !== null && min > max) {
    return { min: max, max: min };
  }
  return { min, max };
};

const INTEGER_INPUT_FORMATTER = new Intl.NumberFormat('pt-BR', {
  maximumFractionDigits: 0,
});

const formatRangeBoundValue = (
  value: number,
  { step = 0.01, kind = 'min' }: { step?: number; kind?: 'min' | 'max' } = {},
) => {
  if (step >= 1) {
    const normalized = kind === 'min' ? Math.floor(value) : Math.ceil(value);
    return INTEGER_INPUT_FORMATTER.format(normalized);
  }

  const factor = 100;
  const rounded = kind === 'min' ? Math.floor(value * factor) / factor : Math.ceil(value * factor) / factor;
  return rounded.toFixed(2);
};

const isFullRange = (
  min: number | null,
  max: number | null,
  bounds: { min: number; max: number } | null | undefined,
) => {
  if (!bounds || min === null || max === null) return false;
  const tolerance = 0.01;
  const coversMin = min <= bounds.min + tolerance;
  const coversMax = max >= bounds.max - tolerance;
  return coversMin && coversMax;
};

const isRangeFilterActive = (
  minInput: string,
  maxInput: string,
  bounds: { min: number; max: number } | null | undefined,
  { integer = false }: { integer?: boolean } = {},
) => {
  const { min, max } = integer ? parseRangeInputInteger(minInput, maxInput) : parseRangeInput(minInput, maxInput);
  if (min === null && max === null) return false;
  if (!bounds) return true;
  return !isFullRange(min, max, bounds);
};

type FetchState = {
  loading: boolean;
  error: string | null;
};

type GridSortColumn = {
  field: string;
  direction: 'Ascending' | 'Descending';
};

type CatalogSavedSearch = {
  id: string;
  name: string;
  description: string | null;
  catalogPath: string;
  queryString: string;
  createdAt?: string;
  updatedAt?: string;
};

type CatalogDefaultSearch = {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  catalogPath: string;
  queryString: string;
};

const DEFAULT_SORT_COLUMNS: GridSortColumn[] = [{ field: 'dy12mPct', direction: 'Descending' }];

const parseSortableNumber = (value: unknown): number | null => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const raw = String(value).trim();
  if (!raw || raw === '-') return null;
  const normalized = raw
    .replace(/\s+/g, '')
    .replace(/[R$%]/g, '')
    .replace(/\./g, '')
    .replace(',', '.')
    .replace(/[^0-9.-]/g, '');
  if (!normalized || normalized === '-' || normalized === '.' || normalized === '-.') return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

export default function CatalogoFundTypeTickers({
  initialFundTypeSymbols,
  initialInstrumentTypeSymbols,
}: {
  initialFundTypeSymbols?: string[];
  initialInstrumentTypeSymbols?: string[];
}) {
  const { data: session } = useSession();
  const isAuthenticated = Boolean(session?.user?.email);
  const PERCENT_2_FORMATTER = useMemo(
    () =>
      new Intl.NumberFormat('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    [],
  );
  const PERCENT_4_FORMATTER = useMemo(
    () =>
      new Intl.NumberFormat('pt-BR', {
        minimumFractionDigits: 4,
        maximumFractionDigits: 4,
      }),
    [],
  );
  const INTEGER_FORMATTER = useMemo(() => new Intl.NumberFormat('pt-BR'), []);
  const CURRENCY_COMPACT_FORMATTER = useMemo(
    () =>
      new Intl.NumberFormat('pt-BR', {
        notation: 'compact',
        compactDisplay: 'short',
        maximumFractionDigits: 2,
      }),
    [],
  );
  const formatPercent = (
    value: number | null | undefined,
    { useFourDecimalsBelowOne = false }: { useFourDecimalsBelowOne?: boolean } = {},
  ) => {
    if (value === null || value === undefined || Number.isNaN(value)) return '-';
    const abs = Math.abs(value);
    if (abs > 0 && abs < 0.01) return `${value < 0 ? '-' : ''}<0,01%`;
    if (useFourDecimalsBelowOne && abs < 1) return `${PERCENT_4_FORMATTER.format(value)}%`;
    return `${PERCENT_2_FORMATTER.format(value)}%`;
  };
  const formatCompactNumber = (value: number | null | undefined) => {
    if (value === null || value === undefined || Number.isNaN(value)) return '-';
    return CURRENCY_COMPACT_FORMATTER.format(value);
  };
  const initialSelection = useMemo(() => {
    const base = initialFundTypeSymbols ?? initialInstrumentTypeSymbols ?? [];
    return Array.from(new Set(base.map((value) => value.trim().toUpperCase()).filter(Boolean)));
  }, [initialFundTypeSymbols, initialInstrumentTypeSymbols]);

  const theme = useTheme();
  const appColors = useAppColors();
  const activeFilterColor = appColors.semantic.violet;
  const activeFilterBorderColor = alpha(activeFilterColor, 0.45);
  const activeFilterBg = alpha(activeFilterColor, theme.palette.mode === 'dark' ? 0.22 : 0.08);
  const activeFilterBgHover = alpha(activeFilterColor, theme.palette.mode === 'dark' ? 0.3 : 0.12);
  const activeFilterInset = `inset 0 0 0 1px ${alpha(activeFilterColor, 0.15)}`;
  const outlinedActionButtonSx = {
    textTransform: 'none',
    fontWeight: 700,
    color: activeFilterColor,
    borderColor: activeFilterBorderColor,
    '&:hover': {
      borderColor: activeFilterColor,
      backgroundColor: activeFilterBg,
    },
  } as const;
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [fundTypes, setInstrumentTypes] = useState<InstrumentTypeOption[]>([]);
  const [fundTypeState, setInstrumentTypeState] = useState<FetchState>({
    loading: true,
    error: null,
  });
  const [baseInstruments, setBaseInstruments] = useState<InstrumentRow[]>([]);
  const [baseInstrumentState, setBaseInstrumentState] = useState<FetchState>({
    loading: true,
    error: null,
  });
  const [selectedInstrumentTypes, setSelectedInstrumentTypes] = useState<string[]>(initialSelection);
  const [volatilityMinInput, setVolatilityMinInput] = useState('');
  const [volatilityMaxInput, setVolatilityMaxInput] = useState('');
  const [currentPvpMinInput, setCurrentPvpMinInput] = useState('');
  const [currentPvpMaxInput, setCurrentPvpMaxInput] = useState('');
  const [dyMesMinInput, setDyMesMinInput] = useState('');
  const [dyMesMaxInput, setDyMesMaxInput] = useState('');
  const [inadimplenciaMinInput, setInadimplenciaMinInput] = useState('');
  const [inadimplenciaMaxInput, setInadimplenciaMaxInput] = useState('');
  const [vacanciaMinInput, setVacanciaMinInput] = useState('');
  const [vacanciaMaxInput, setVacanciaMaxInput] = useState('');
  const [cashPressureMonths12mMinInput, setCashPressureMonths12mMinInput] = useState('');
  const [cashPressureMonths12mMaxInput, setCashPressureMonths12mMaxInput] = useState('');
  const [concentracaoTijoloMinInput, setConcentracaoTijoloMinInput] = useState('');
  const [concentracaoTijoloMaxInput, setConcentracaoTijoloMaxInput] = useState('');
  const [concentracaoPapelMinInput, setConcentracaoPapelMinInput] = useState('');
  const [concentracaoPapelMaxInput, setConcentracaoPapelMaxInput] = useState('');
  const [liquidezDiaria30dMinInput, setLiquidezDiaria30dMinInput] = useState('');
  const [liquidezDiaria30dMaxInput, setLiquidezDiaria30dMaxInput] = useState('');
  const [selicSensitivityScoreMinInput, setSelicSensitivityScoreMinInput] = useState('');
  const [selicSensitivityScoreMaxInput, setSelicSensitivityScoreMaxInput] = useState('');
  const [leverageIndexScoreMinInput, setLeverageIndexScoreMinInput] = useState('');
  const [leverageIndexScoreMaxInput, setLeverageIndexScoreMaxInput] = useState('');
  const [distanceTo12mHighMinInput, setDistanceTo12mHighMinInput] = useState('');
  const [distanceTo12mHighMaxInput, setDistanceTo12mHighMaxInput] = useState('');
  const [vpGrowth12mMinInput, setVpGrowth12mMinInput] = useState('');
  const [vpGrowth12mMaxInput, setVpGrowth12mMaxInput] = useState('');
  const [patrimonioMinInput, setPatrimonioMinInput] = useState('');
  const [patrimonioMaxInput, setPatrimonioMaxInput] = useState('');
  const [cotistasMinInput, setCotistasMinInput] = useState('');
  const [cotistasMaxInput, setCotistasMaxInput] = useState('');
  const [classificacaoFilter, setClassificacaoFilter] = useState('');
  const [segmentoFilter, setSegmentoFilter] = useState('');
  const [cashTrendStatusFilter, setCashTrendStatusFilter] = useState('');
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const [fundTypeDialogOpen, setInstrumentTypeDialogOpen] = useState(false);
  const [columnsDialogOpen, setColumnsDialogOpen] = useState(false);
  const [hiddenColumnFields, setHiddenColumnFields] = useState<string[]>([]);
  const [apiFilteredInstruments, setApiFilteredInstruments] = useState<InstrumentRow[]>([]);
  const [hasLoadedApiFilters, setHasLoadedApiFilters] = useState(false);
  const [apiFilterState, setApiFilterState] = useState<FetchState>({
    loading: false,
    error: null,
  });
  const [defaultSearches, setDefaultSearches] = useState<CatalogDefaultSearch[]>([]);
  const [defaultSearchesState, setDefaultSearchesState] = useState<FetchState>({
    loading: true,
    error: null,
  });
  const [savedSearches, setSavedSearches] = useState<CatalogSavedSearch[]>([]);
  const [savedSearchesState, setSavedSearchesState] = useState<FetchState>({
    loading: false,
    error: null,
  });
  const [selectedDefaultSearchId, setSelectedDefaultSearchId] = useState('');
  const [selectedSavedSearchId, setSelectedSavedSearchId] = useState('');
  const [loadSearchDialogOpen, setLoadSearchDialogOpen] = useState(false);
  const [loginRequiredDialogOpen, setLoginRequiredDialogOpen] = useState(false);
  const [saveSearchDialogOpen, setSaveSearchDialogOpen] = useState(false);
  const [saveSearchName, setSaveSearchName] = useState('');
  const [saveSearchDescription, setSaveSearchDescription] = useState('');
  const [saveSearchError, setSaveSearchError] = useState<string | null>(null);
  const [saveSearchBusy, setSaveSearchBusy] = useState(false);
  const [savedSearchActionMessage, setSavedSearchActionMessage] = useState<string | null>(null);
  const [sortColumns, setSortColumns] = useState<GridSortColumn[]>(DEFAULT_SORT_COLUMNS);
  const multiSortModifierLabel = useMemo(() => {
    if (typeof navigator === 'undefined') return 'Ctrl';
    return /(Mac|iPhone|iPad|iPod)/i.test(navigator.platform) ? '⌘' : 'Ctrl';
  }, []);
  const sortDirectionByFieldRef = useRef<Record<string, 'Ascending' | 'Descending'>>({});
  useEffect(() => {
    const next: Record<string, 'Ascending' | 'Descending'> = {};
    sortColumns.forEach((column) => {
      next[column.field] = column.direction;
    });
    sortDirectionByFieldRef.current = next;
  }, [sortColumns]);

  const makeNumericSortComparer = useCallback(
    (field: string) => (reference: unknown, comparer: unknown) => {
      const leftValue = parseSortableNumber(reference);
      const rightValue = parseSortableNumber(comparer);
      const sortAsc = (sortDirectionByFieldRef.current[field] ?? 'Ascending') === 'Ascending';

      if (sortAsc && leftValue === null) return 1;
      if (sortAsc && rightValue === null) return -1;
      if (!sortAsc && leftValue === null) return -1;
      if (!sortAsc && rightValue === null) return 1;
      if (leftValue === null && rightValue === null) return 0;
      if (leftValue === null || rightValue === null) return 0;

      return leftValue - rightValue;
    },
    [],
  );
  const gridRef = useRef<GridComponent | null>(null);
  const hasHydratedFromUrlRef = useRef(false);
  const hasSkippedInitialUrlCompactionRef = useRef(false);
  const hasAppliedStrategyFromUrlRef = useRef(false);

  const getCurrentCatalogLocation = useCallback(() => {
    if (typeof window === 'undefined') {
      return { catalogPath: '/catalogo', queryString: '' };
    }
    const queryString = window.location.search.startsWith('?')
      ? window.location.search.slice(1)
      : window.location.search;
    return {
      catalogPath: window.location.pathname || '/catalogo',
      queryString,
    };
  }, []);

  const removeSortColumn = useCallback((field: string) => {
    setSortColumns((current) => {
      const next = current.filter((column) => column.field !== field);
      return next.length > 0 ? next : [...DEFAULT_SORT_COLUMNS];
    });
  }, []);

  const applyFiltersFromUrlParams = useCallback(
    (params: URLSearchParams) => {
      const compact = decodeCatalogoCompactFromParams(params);
      const parseInstrumentTypes = () => {
        const raw = compact?.filters.fundTypes;
        if (!raw) return null;
        const next = Array.from(
          new Set(
            raw
              .split(',')
              .map((value) => value.trim().toUpperCase())
              .filter(Boolean),
          ),
        );
        return next;
      };

      const parseText = (key: string) => (compact?.filters[key] ?? '').trim();

      const fundTypesFromUrl = parseInstrumentTypes();
      if (fundTypesFromUrl) {
        setSelectedInstrumentTypes(fundTypesFromUrl);
      } else {
        setSelectedInstrumentTypes(initialSelection);
      }

      setClassificacaoFilter(parseText('classificacao').toUpperCase());
      setSegmentoFilter(parseText('segmento'));
      setCashTrendStatusFilter(parseText('cashTrendStatus'));

      setVolatilityMinInput(parseText('volatilityMin'));
      setVolatilityMaxInput(parseText('volatilityMax'));
      setCurrentPvpMinInput(parseText('currentPvpMin'));
      setCurrentPvpMaxInput(parseText('currentPvpMax'));
      setDyMesMinInput(parseText('dy12mMin'));
      setDyMesMaxInput(parseText('dy12mMax'));
      setInadimplenciaMinInput(parseText('inadimplenciaMin'));
      setInadimplenciaMaxInput(parseText('inadimplenciaMax'));
      setVacanciaMinInput(parseText('vacanciaMin'));
      setVacanciaMaxInput(parseText('vacanciaMax'));
      setCashPressureMonths12mMinInput(parseText('cashPressureMonths12mMin'));
      setCashPressureMonths12mMaxInput(parseText('cashPressureMonths12mMax'));
      setConcentracaoTijoloMinInput(parseText('concentracaoTijoloMin'));
      setConcentracaoTijoloMaxInput(parseText('concentracaoTijoloMax'));
      setConcentracaoPapelMinInput(parseText('concentracaoPapelMin'));
      setConcentracaoPapelMaxInput(parseText('concentracaoPapelMax'));
      setLiquidezDiaria30dMinInput(parseText('liquidezDiaria30dMin'));
      setLiquidezDiaria30dMaxInput(parseText('liquidezDiaria30dMax'));
      setSelicSensitivityScoreMinInput(parseText('selicSensitivityScoreMin'));
      setSelicSensitivityScoreMaxInput(parseText('selicSensitivityScoreMax'));
      setLeverageIndexScoreMinInput(parseText('leverageIndexScoreMin'));
      setLeverageIndexScoreMaxInput(parseText('leverageIndexScoreMax'));
      setDistanceTo12mHighMinInput(parseText('distanceTo12mHighMin'));
      setDistanceTo12mHighMaxInput(parseText('distanceTo12mHighMax'));
      setVpGrowth12mMinInput(parseText('vpPerShareGrowth12mMin'));
      setVpGrowth12mMaxInput(parseText('vpPerShareGrowth12mMax'));
      setPatrimonioMinInput(parseText('patrimonioMin'));
      setPatrimonioMaxInput(parseText('patrimonioMax'));
      setCotistasMinInput(parseText('cotistasMin'));
      setCotistasMaxInput(parseText('cotistasMax'));
      const decodedSortColumns = compact?.sortColumns ?? [];
      if (decodedSortColumns.length > 0) {
        setSortColumns(decodedSortColumns);
      } else {
        setSortColumns([...DEFAULT_SORT_COLUMNS]);
      }
      if (compact?.visibleColumns && compact.visibleColumns.length > 0) {
        const visible = new Set(compact.visibleColumns);
        const hidden = CATALOGO_ALL_COLUMN_FIELDS.filter((field) => !visible.has(field));
        setHiddenColumnFields(hidden);
      }
    },
    [initialSelection],
  );

  const applySavedSearch = useCallback(
    (search: { catalogPath: string; queryString: string }) => {
      if (typeof window === 'undefined') return;
      const queryString = search.queryString.trim();
      const nextUrl = queryString ? `${search.catalogPath}?${queryString}` : search.catalogPath;
      window.history.replaceState(null, '', nextUrl);
      applyFiltersFromUrlParams(new URLSearchParams(queryString));
    },
    [applyFiltersFromUrlParams],
  );

  useEffect(() => {
    let cancelled = false;
    const loadInstrumentTypes = async () => {
      setInstrumentTypeState({ loading: true, error: null });
      try {
        const res = await fetch('/api/catalogo/fund-types');
        if (!res.ok) {
          throw new Error(`Erro ao carregar tipos (${res.status})`);
        }
        const data = (await res.json()) as InstrumentTypeOption[];
        if (!cancelled) {
          setInstrumentTypes(data);
          setInstrumentTypeState({ loading: false, error: null });
        }
      } catch (err: any) {
        if (!cancelled) {
          setInstrumentTypeState({
            loading: false,
            error: err?.message ?? 'Falha ao carregar tipos',
          });
        }
      }
    };

    loadInstrumentTypes();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadSavedSearches = useCallback(async () => {
    if (!isAuthenticated) {
      setSavedSearches([]);
      setSelectedSavedSearchId('');
      setSavedSearchesState({ loading: false, error: null });
      return;
    }
    setSavedSearchesState({ loading: true, error: null });
    try {
      const res = await fetch('/api/catalogo/saved-searches', {
        cache: 'no-store',
      });
      if (!res.ok) {
        throw new Error(`Erro ao carregar buscas salvas (${res.status})`);
      }
      const data = (await res.json()) as CatalogSavedSearch[];
      setSavedSearches(data);
      setSavedSearchesState({ loading: false, error: null });
      setSelectedSavedSearchId((current) => (data.some((row) => row.id === current) ? current : ''));
    } catch (err: any) {
      setSavedSearches([]);
      setSelectedSavedSearchId('');
      setSavedSearchesState({
        loading: false,
        error: err?.message ?? 'Falha ao carregar buscas salvas',
      });
    }
  }, [isAuthenticated]);

  useEffect(() => {
    let cancelled = false;
    const loadDefaultSearches = async () => {
      setDefaultSearchesState({ loading: true, error: null });
      try {
        const res = await fetch('/api/catalogo/default-searches', {
          cache: 'no-store',
        });
        if (!res.ok) {
          throw new Error(`Erro ao carregar buscas padrão (${res.status})`);
        }
        const data = (await res.json()) as CatalogDefaultSearch[];
        if (cancelled) return;
        setDefaultSearches(data);
        setDefaultSearchesState({ loading: false, error: null });
      } catch (err: any) {
        if (cancelled) return;
        setDefaultSearches([]);
        setDefaultSearchesState({
          loading: false,
          error: err?.message ?? 'Falha ao carregar buscas padrão',
        });
      }
    };

    loadDefaultSearches();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    loadSavedSearches();
  }, [loadSavedSearches]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    applyFiltersFromUrlParams(params);
    hasHydratedFromUrlRef.current = true;
  }, [applyFiltersFromUrlParams]);

  const normalizedSelectedInstrumentTypes = useMemo(
    () => selectedInstrumentTypes.map((symbol) => symbol.trim().toUpperCase()).filter(Boolean),
    [selectedInstrumentTypes],
  );
  const fundTypeDisplayValue = useMemo(() => {
    if (normalizedSelectedInstrumentTypes.length === 0) return 'Todos os tipos';
    if (normalizedSelectedInstrumentTypes.length <= 2) {
      return normalizedSelectedInstrumentTypes.join(', ');
    }
    return `${normalizedSelectedInstrumentTypes.slice(0, 2).join(', ')} +${normalizedSelectedInstrumentTypes.length - 2}`;
  }, [normalizedSelectedInstrumentTypes]);
  const selectedDefaultSearch = useMemo(
    () => defaultSearches.find((row) => row.id === selectedDefaultSearchId) ?? null,
    [defaultSearches, selectedDefaultSearchId],
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (hasAppliedStrategyFromUrlRef.current) return;
    if (defaultSearches.length === 0) return;

    const params = new URLSearchParams(window.location.search);
    const strategyId = (params.get('strategy') ?? params.get('st') ?? '').trim();
    if (!strategyId) return;

    const hasOtherParams = Array.from(params.keys()).some((key) => key !== 'strategy');
    if (hasOtherParams) return;

    const strategy = defaultSearches.find((row) => row.id === strategyId);
    if (!strategy) return;

    hasAppliedStrategyFromUrlRef.current = true;
    applySavedSearch(strategy);
    setSelectedDefaultSearchId(strategy.id);
  }, [applySavedSearch, defaultSearches]);
  const selectedSavedSearch = useMemo(
    () => savedSearches.find((row) => row.id === selectedSavedSearchId) ?? null,
    [savedSearches, selectedSavedSearchId],
  );
  const currentLoadedSearchLabel = useMemo(() => {
    if (selectedSavedSearch) {
      return `Minha busca: ${selectedSavedSearch.name}`;
    }
    if (selectedDefaultSearch) {
      return `Estratégia pronta: ${selectedDefaultSearch.name}`;
    }
    return null;
  }, [selectedDefaultSearch, selectedSavedSearch]);
  const baseQueryString = useMemo(() => {
    return buildCatalogoBaseQueryString(normalizedSelectedInstrumentTypes);
  }, [normalizedSelectedInstrumentTypes]);

  const openInstrumentTypeDialog = useCallback(() => {
    setInstrumentTypeDialogOpen(true);
  }, []);

  const toggleSelectedInstrumentType = useCallback((symbol: string) => {
    setSelectedInstrumentTypes((current) =>
      current.includes(symbol) ? current.filter((item) => item !== symbol) : [...current, symbol],
    );
  }, []);

  const columns = useMemo(
    () => [
      {
        field: 'fundTypeSymbol',
        headerText: 'Tipo',
        width: isMobile ? 64 : 72,
        minWidth: isMobile ? 56 : 68,
        maxWidth: isMobile ? 72 : 80,
      },
      {
        field: 'idCem',
        headerText: 'Ticker',
        width: isMobile ? 82 : 92,
        minWidth: isMobile ? 72 : 84,
        maxWidth: isMobile ? 92 : 104,
        clipMode: 'Ellipsis',
        template: (row: InstrumentRow) => (
          <AppLink
            href={`/ativo/${encodeURIComponent(displayInstrument(row.idCem, row.fundTypeSymbol))}`}
            underline='hover'
            sx={{
              fontWeight: 700,
              color: appColors.actions.accentOutline.text,
              '&:hover': {
                color: appColors.actions.accentOutline.hoverText,
              },
            }}
          >
            {displayInstrument(row.idCem, row.fundTypeSymbol)}
          </AppLink>
        ),
      },
      {
        field: 'name',
        headerText: 'Nome',
        width: isMobile ? 170 : 220,
        minWidth: isMobile ? 140 : 190,
        maxWidth: isMobile ? 210 : 240,
        clipMode: 'Ellipsis',
        template: (row: InstrumentRow) => (
          <Tooltip title={row.name ?? '-'} placement='top' arrow enterDelay={400} leaveDelay={100}>
            <Typography
              variant='body2'
              sx={{
                fontWeight: 600,
                color: 'text.primary',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '100%',
              }}
            >
              {row.name ?? '-'}
            </Typography>
          </Tooltip>
        ),
      },
      {
        field: 'dy12mPct',
        headerText: 'DY 12m',
        width: isMobile ? 90 : 96,
        minWidth: isMobile ? 82 : 90,
        maxWidth: isMobile ? 100 : 106,
        sortComparer: makeNumericSortComparer('dy12mPct'),
        template: (row: InstrumentRow) => (
          <Typography variant='body2' color='text.secondary'>
            {formatPercent(row.dy12mPct, { useFourDecimalsBelowOne: true })}
          </Typography>
        ),
      },
      {
        field: 'currentPvp',
        headerText: 'P/VP',
        width: isMobile ? 90 : 96,
        minWidth: isMobile ? 82 : 90,
        maxWidth: isMobile ? 100 : 106,
        sortComparer: makeNumericSortComparer('currentPvp'),
        template: (row: InstrumentRow) => (
          <Typography variant='body2' color='text.secondary'>
            {row.currentPvp === null || row.currentPvp === undefined ? '-' : PERCENT_4_FORMATTER.format(row.currentPvp)}
          </Typography>
        ),
      },
      {
        field: 'numeroCotistas',
        headerText: 'Cotistas',
        width: isMobile ? 94 : 104,
        minWidth: isMobile ? 86 : 96,
        maxWidth: isMobile ? 108 : 116,
        sortComparer: makeNumericSortComparer('numeroCotistas'),
        template: (row: InstrumentRow) => (
          <Typography variant='body2' color='text.secondary'>
            {row.numeroCotistas === null || row.numeroCotistas === undefined
              ? '-'
              : INTEGER_FORMATTER.format(row.numeroCotistas)}
          </Typography>
        ),
      },
      {
        field: 'vacanciaPct',
        headerText: 'Vacância',
        width: isMobile ? 90 : 96,
        minWidth: isMobile ? 82 : 90,
        maxWidth: isMobile ? 100 : 106,
        sortComparer: makeNumericSortComparer('vacanciaPct'),
        template: (row: InstrumentRow) => (
          <Typography variant='body2' color='text.secondary'>
            {formatPercent(row.vacanciaPct, { useFourDecimalsBelowOne: true })}
          </Typography>
        ),
      },
      {
        field: 'inadimplenciaPct',
        headerText: 'Inadimplência',
        width: isMobile ? 94 : 104,
        minWidth: isMobile ? 84 : 96,
        maxWidth: isMobile ? 110 : 116,
        sortComparer: makeNumericSortComparer('inadimplenciaPct'),
        template: (row: InstrumentRow) => (
          <Typography variant='body2' color='text.secondary'>
            {formatPercent(row.inadimplenciaPct, {
              useFourDecimalsBelowOne: true,
            })}
          </Typography>
        ),
      },
      {
        field: 'volatilityAnnualized21dPct',
        headerText: 'Vol. 21d',
        width: isMobile ? 100 : 112,
        minWidth: isMobile ? 90 : 102,
        maxWidth: isMobile ? 116 : 124,
        sortComparer: makeNumericSortComparer('volatilityAnnualized21dPct'),
        template: (row: InstrumentRow) => (
          <Typography variant='body2' color='text.secondary'>
            {formatPercent(row.volatilityAnnualized21dPct)}
          </Typography>
        ),
      },
      {
        field: 'cashPressureMonths12m',
        headerText: 'Press. 12m',
        width: isMobile ? 84 : 90,
        minWidth: isMobile ? 76 : 84,
        maxWidth: isMobile ? 92 : 96,
        sortComparer: makeNumericSortComparer('cashPressureMonths12m'),
        template: (row: InstrumentRow) => (
          <Typography variant='body2' color='text.secondary'>
            {row.cashPressureMonths12m === null || row.cashPressureMonths12m === undefined
              ? '-'
              : INTEGER_FORMATTER.format(row.cashPressureMonths12m)}
          </Typography>
        ),
      },
      {
        field: 'cashTrendStatus',
        headerText: 'Caixa',
        width: isMobile ? 114 : 124,
        minWidth: isMobile ? 102 : 112,
        maxWidth: isMobile ? 126 : 134,
        clipMode: 'Ellipsis',
        template: (row: InstrumentRow) => (
          <Typography variant='body2' color='text.secondary'>
            {row.cashTrendStatusLabel ?? row.cashTrendStatus ?? '-'}
          </Typography>
        ),
      },
      {
        field: 'distanceTo12mHighPct',
        headerText: 'Dist. 12m',
        width: isMobile ? 94 : 102,
        minWidth: isMobile ? 84 : 94,
        maxWidth: isMobile ? 106 : 112,
        sortComparer: makeNumericSortComparer('distanceTo12mHighPct'),
        template: (row: InstrumentRow) => (
          <Typography variant='body2' color='text.secondary'>
            {formatPercent(row.distanceTo12mHighPct)}
          </Typography>
        ),
      },
      {
        field: 'concentracaoTijoloPct',
        headerText: 'Conc. Tijolo',
        width: isMobile ? 94 : 102,
        minWidth: isMobile ? 84 : 94,
        maxWidth: isMobile ? 106 : 112,
        sortComparer: makeNumericSortComparer('concentracaoTijoloPct'),
        template: (row: InstrumentRow) => (
          <Typography variant='body2' color='text.secondary'>
            {formatPercent(row.concentracaoTijoloPct)}
          </Typography>
        ),
      },
      {
        field: 'concentracaoPapelPct',
        headerText: 'Conc. Papel',
        width: isMobile ? 94 : 102,
        minWidth: isMobile ? 84 : 94,
        maxWidth: isMobile ? 106 : 112,
        sortComparer: makeNumericSortComparer('concentracaoPapelPct'),
        template: (row: InstrumentRow) => (
          <Typography variant='body2' color='text.secondary'>
            {formatPercent(row.concentracaoPapelPct)}
          </Typography>
        ),
      },
      {
        field: 'liquidezDiaria30d',
        headerText: 'Liq. 30d',
        width: isMobile ? 106 : 116,
        minWidth: isMobile ? 96 : 106,
        maxWidth: isMobile ? 118 : 126,
        sortComparer: makeNumericSortComparer('liquidezDiaria30d'),
        template: (row: InstrumentRow) => (
          <Typography variant='body2' color='text.secondary'>
            {row.liquidezDiaria30d === null || row.liquidezDiaria30d === undefined
              ? '-'
              : `R$ ${formatCompactNumber(row.liquidezDiaria30d)}`}
          </Typography>
        ),
      },
      {
        field: 'vpPerShareGrowth12mPct',
        headerText: 'Cresc. VP/cota',
        width: isMobile ? 100 : 110,
        minWidth: isMobile ? 90 : 100,
        maxWidth: isMobile ? 112 : 120,
        sortComparer: makeNumericSortComparer('vpPerShareGrowth12mPct'),
        template: (row: InstrumentRow) => (
          <Typography variant='body2' color='text.secondary'>
            {formatPercent(row.vpPerShareGrowth12mPct)}
          </Typography>
        ),
      },
      {
        field: 'patrimonioLiquido',
        headerText: 'Patrimônio',
        width: isMobile ? 100 : 110,
        minWidth: isMobile ? 90 : 100,
        maxWidth: isMobile ? 112 : 120,
        sortComparer: makeNumericSortComparer('patrimonioLiquido'),
        template: (row: InstrumentRow) => (
          <Typography variant='body2' color='text.secondary'>
            {formatCompactNumber(row.patrimonioLiquido)}
          </Typography>
        ),
      },
      {
        field: 'classificacao',
        headerText: 'Classificação',
        width: isMobile ? 106 : 116,
        minWidth: isMobile ? 94 : 106,
        maxWidth: isMobile ? 118 : 126,
        clipMode: 'Ellipsis',
        template: (row: InstrumentRow) => {
          const label = row.classificacaoLabel ?? row.classificacao ?? '-';
          return (
            <Tooltip title={label} placement='top' arrow enterDelay={400} leaveDelay={100}>
              <Typography
                variant='body2'
                color='text.secondary'
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '100%',
                }}
              >
                {label}
              </Typography>
            </Tooltip>
          );
        },
      },
      {
        field: 'segmento',
        headerText: 'Segmento',
        width: isMobile ? 112 : 124,
        minWidth: isMobile ? 100 : 112,
        maxWidth: isMobile ? 126 : 136,
        clipMode: 'Ellipsis',
        template: (row: InstrumentRow) => {
          const label = row.segmento ?? '-';
          return (
            <Tooltip title={label} placement='top' arrow enterDelay={400} leaveDelay={100}>
              <Typography
                variant='body2'
                color='text.secondary'
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '100%',
                }}
              >
                {label}
              </Typography>
            </Tooltip>
          );
        },
      },
      {
        field: 'selicSensitivityScore',
        headerText: 'Selic',
        width: isMobile ? 84 : 92,
        minWidth: isMobile ? 76 : 86,
        maxWidth: isMobile ? 94 : 100,
        sortComparer: makeNumericSortComparer('selicSensitivityScore'),
        template: (row: InstrumentRow) => (
          <Typography variant='body2' color='text.secondary'>
            {formatPercent(row.selicSensitivityScore)}
          </Typography>
        ),
      },
      {
        field: 'leverageIndexScore',
        headerText: 'Alav.',
        width: isMobile ? 84 : 92,
        minWidth: isMobile ? 76 : 86,
        maxWidth: isMobile ? 94 : 100,
        sortComparer: makeNumericSortComparer('leverageIndexScore'),
        template: (row: InstrumentRow) => (
          <Typography variant='body2' color='text.secondary'>
            {formatPercent(row.leverageIndexScore)}
          </Typography>
        ),
      },
    ],
    [
      CURRENCY_COMPACT_FORMATTER,
      INTEGER_FORMATTER,
      PERCENT_2_FORMATTER,
      PERCENT_4_FORMATTER,
      isMobile,
      theme.palette.mode,
    ],
  );

  const columnChooserOptions = useMemo(
    () =>
      columns
        .map((column) => {
          const field = String(column.field ?? '').trim();
          if (!field) return null;
          return {
            field,
            label: COLUMN_CHOOSER_FULL_LABELS[field] ?? String(column.headerText ?? field),
          };
        })
        .filter(
          (
            item,
          ): item is {
            field: string;
            label: string;
          } => item !== null,
        ),
    [columns],
  );

  const headerTooltipByHeaderText = useMemo(() => {
    const map = new Map<string, string>();
    columns.forEach((column) => {
      const field = String(column.field ?? '').trim();
      const headerText = String(column.headerText ?? '').trim();
      if (!field || !headerText) return;
      const fullLabel = COLUMN_CHOOSER_FULL_LABELS[field] ?? headerText;
      map.set(headerText, fullLabel);
    });
    return map;
  }, [columns]);

  useEffect(() => {
    const validFields = new Set(columnChooserOptions.map((item) => item.field));
    setHiddenColumnFields((current) => current.filter((field) => validFields.has(field)));
  }, [columnChooserOptions]);

  const toggleColumnVisibility = useCallback(
    (field: string) => {
      setHiddenColumnFields((current) => {
        const isHidden = current.includes(field);
        if (isHidden) {
          return current.filter((item) => item !== field);
        }
        const visibleCount = columnChooserOptions.length - current.length;
        if (visibleCount <= 1) return current;
        return [...current, field];
      });
    },
    [columnChooserOptions.length],
  );

  const showAllColumns = useCallback(() => {
    setHiddenColumnFields([]);
  }, []);

  const invertColumnSelection = useCallback(() => {
    setHiddenColumnFields((current) => {
      const currentHidden = new Set(current);
      let nextHidden = columnChooserOptions.map((option) => option.field).filter((field) => !currentHidden.has(field));

      if (nextHidden.length >= columnChooserOptions.length) {
        const [firstField] = columnChooserOptions;
        if (firstField) {
          nextHidden = nextHidden.filter((field) => field !== firstField.field);
        }
      }

      return nextHidden;
    });
  }, [columnChooserOptions]);

  useEffect(() => {
    if (!gridRef.current) return;
    const allFields = columnChooserOptions.map((item) => item.field);
    const hidden = new Set(hiddenColumnFields);
    const visibleFields = allFields.filter((field) => !hidden.has(field));

    if (hiddenColumnFields.length > 0) {
      gridRef.current.hideColumns(hiddenColumnFields as string[], 'field');
    }
    if (visibleFields.length > 0) {
      gridRef.current.showColumns(visibleFields as string[], 'field');
    }
    if (!isMobile && visibleFields.length > 0) {
      gridRef.current.autoFitColumns(visibleFields);
    }
  }, [columnChooserOptions, hiddenColumnFields, isMobile]);

  const applyGridColumnVisibility = useCallback(() => {
    if (!gridRef.current) return;
    const allFields = columnChooserOptions.map((item) => item.field);
    const hidden = new Set(hiddenColumnFields);
    const visibleFields = allFields.filter((field) => !hidden.has(field));

    if (hiddenColumnFields.length > 0) {
      gridRef.current.hideColumns(hiddenColumnFields as string[], 'field');
    }
    if (visibleFields.length > 0) {
      gridRef.current.showColumns(visibleFields as string[], 'field');
      if (!isMobile) {
        gridRef.current.autoFitColumns(visibleFields);
      }
    }
  }, [columnChooserOptions, hiddenColumnFields, isMobile]);

  const applyGridHeaderTooltips = useCallback(() => {
    const grid = gridRef.current;
    const headerTable = grid?.getHeaderTable?.();
    if (!headerTable) return;
    const headerCells = headerTable.querySelectorAll('th.e-headercell');
    headerCells.forEach((cell) => {
      const labelNode = cell.querySelector('.e-headertext');
      const text = (labelNode?.textContent ?? '').trim();
      if (!text) return;
      const baseLabel = headerTooltipByHeaderText.get(text) ?? text;
      cell.setAttribute(
        'title',
        `${baseLabel}. Clique para ordenar. ${multiSortModifierLabel} + clique para multi-ordenação.`,
      );
    });
  }, [headerTooltipByHeaderText, multiSortModifierLabel]);

  const optionsBaseRows = useMemo(() => {
    const hasMetadataInSelection = baseInstruments.some(
      (ticker) => (ticker.classificacao ?? '').trim() || (ticker.segmento ?? '').trim() || (ticker.gestao ?? '').trim(),
    );
    return hasMetadataInSelection ? baseInstruments : apiFilteredInstruments;
  }, [apiFilteredInstruments, baseInstruments]);

  const classificacaoLabelByValue = useMemo(() => {
    const entries = optionsBaseRows
      .map((ticker) => {
        const value = (ticker.classificacao ?? '').trim();
        if (!value) return null;
        return [value, ticker.classificacaoLabel?.trim() || value] as const;
      })
      .filter((entry): entry is readonly [string, string] => entry !== null);
    return new Map(entries);
  }, [optionsBaseRows]);

  const cashTrendLabelByValue = useMemo(() => {
    const entries = optionsBaseRows
      .map((ticker) => {
        const value = (ticker.cashTrendStatus ?? '').trim();
        if (!value) return null;
        return [value, ticker.cashTrendStatusLabel?.trim() || value] as const;
      })
      .filter((entry): entry is readonly [string, string] => entry !== null);
    return new Map(entries);
  }, [optionsBaseRows]);

  const sliderRanges = useMemo(
    () => ({
      volatility: computeRange(baseInstruments, (row) => row.volatilityAnnualized21dPct, {
        clampMin: 0,
        clampMax: 120,
      }),
      currentPvp: computeRange(baseInstruments, (row) => row.currentPvp, {
        clampMin: 0,
        clampMax: 5,
      }),
      dyMes: computeRange(baseInstruments, (row) => row.dy12mPct, {
        clampMin: -5,
        trimPercentile: 0.02,
      }),
      inadimplencia: computeRange(baseInstruments, (row) => row.inadimplenciaPct, {
        clampMin: 0,
        clampMax: 100,
      }),
      vacancia: computeRange(baseInstruments, (row) => row.vacanciaPct, {
        clampMin: 0,
        clampMax: 100,
      }),
      cashPressureMonths12m: computeRange(baseInstruments, (row) => row.cashPressureMonths12m, {
        clampMin: 0,
        clampMax: 12,
      }),
      concentracaoTijolo: computeRange(baseInstruments, (row) => row.concentracaoTijoloPct, {
        clampMin: 0,
        clampMax: 100,
      }),
      concentracaoPapel: computeRange(baseInstruments, (row) => row.concentracaoPapelPct, {
        clampMin: 0,
        clampMax: 100,
      }),
      liquidezDiaria30d: computeRange(baseInstruments, (row) => row.liquidezDiaria30d, {
        clampMin: 0,
      }),
      selicSensitivityScore: computeRange(baseInstruments, (row) => row.selicSensitivityScore, {
        clampMin: 0,
        clampMax: 100,
      }),
      leverageIndexScore: computeRange(baseInstruments, (row) => row.leverageIndexScore, {
        clampMin: 0,
        clampMax: 100,
      }),
      distanceTo12mHigh: computeRange(baseInstruments, (row) => row.distanceTo12mHighPct, {
        clampMin: 0,
        clampMax: 100,
      }),
      vpGrowth12m: computeRange(baseInstruments, (row) => row.vpPerShareGrowth12mPct, {
        clampMin: -100,
        clampMax: 300,
      }),
      patrimonio: computeRange(baseInstruments, (row) => row.patrimonioLiquido, {
        clampMin: 0,
      }),
      cotistas: computeRange(baseInstruments, (row) => row.numeroCotistas, {
        clampMin: 0,
      }),
    }),
    [baseInstruments],
  );

  const apiRangeActive = useMemo(
    () => ({
      currentPvp: isRangeFilterActive(currentPvpMinInput, currentPvpMaxInput, sliderRanges.currentPvp),
      dy12m: isRangeFilterActive(dyMesMinInput, dyMesMaxInput, sliderRanges.dyMes),
      inadimplencia: isRangeFilterActive(inadimplenciaMinInput, inadimplenciaMaxInput, sliderRanges.inadimplencia),
      vacancia: isRangeFilterActive(vacanciaMinInput, vacanciaMaxInput, sliderRanges.vacancia),
      patrimonio: isRangeFilterActive(patrimonioMinInput, patrimonioMaxInput, sliderRanges.patrimonio, {
        integer: true,
      }),
      cotistas: isRangeFilterActive(cotistasMinInput, cotistasMaxInput, sliderRanges.cotistas, { integer: true }),
      volatility: isRangeFilterActive(volatilityMinInput, volatilityMaxInput, sliderRanges.volatility),
      distanceTo12mHigh: isRangeFilterActive(
        distanceTo12mHighMinInput,
        distanceTo12mHighMaxInput,
        sliderRanges.distanceTo12mHigh,
      ),
      cashPressureMonths12m: isRangeFilterActive(
        cashPressureMonths12mMinInput,
        cashPressureMonths12mMaxInput,
        sliderRanges.cashPressureMonths12m,
        { integer: true },
      ),
      concentracaoTijolo: isRangeFilterActive(
        concentracaoTijoloMinInput,
        concentracaoTijoloMaxInput,
        sliderRanges.concentracaoTijolo,
      ),
      concentracaoPapel: isRangeFilterActive(
        concentracaoPapelMinInput,
        concentracaoPapelMaxInput,
        sliderRanges.concentracaoPapel,
      ),
      liquidezDiaria30d: isRangeFilterActive(
        liquidezDiaria30dMinInput,
        liquidezDiaria30dMaxInput,
        sliderRanges.liquidezDiaria30d,
        { integer: true },
      ),
      selicSensitivityScore: isRangeFilterActive(
        selicSensitivityScoreMinInput,
        selicSensitivityScoreMaxInput,
        sliderRanges.selicSensitivityScore,
      ),
      leverageIndexScore: isRangeFilterActive(
        leverageIndexScoreMinInput,
        leverageIndexScoreMaxInput,
        sliderRanges.leverageIndexScore,
      ),
      vpGrowth12m: isRangeFilterActive(vpGrowth12mMinInput, vpGrowth12mMaxInput, sliderRanges.vpGrowth12m),
    }),
    [
      cashPressureMonths12mMaxInput,
      cashPressureMonths12mMinInput,
      concentracaoPapelMaxInput,
      concentracaoPapelMinInput,
      concentracaoTijoloMaxInput,
      concentracaoTijoloMinInput,
      cotistasMaxInput,
      cotistasMinInput,
      currentPvpMaxInput,
      currentPvpMinInput,
      distanceTo12mHighMaxInput,
      distanceTo12mHighMinInput,
      dyMesMaxInput,
      dyMesMinInput,
      inadimplenciaMaxInput,
      inadimplenciaMinInput,
      liquidezDiaria30dMaxInput,
      liquidezDiaria30dMinInput,
      leverageIndexScoreMaxInput,
      leverageIndexScoreMinInput,
      patrimonioMaxInput,
      patrimonioMinInput,
      selicSensitivityScoreMaxInput,
      selicSensitivityScoreMinInput,
      sliderRanges,
      vacanciaMaxInput,
      vacanciaMinInput,
      volatilityMaxInput,
      volatilityMinInput,
      vpGrowth12mMaxInput,
      vpGrowth12mMinInput,
    ],
  );

  const apiFilterQueryString = useMemo(() => {
    const params = new URLSearchParams();
    const put = (key: string, value: string) => {
      const normalized = value.trim();
      if (normalized) params.set(key, normalized);
    };

    if (normalizedSelectedInstrumentTypes.length > 0) {
      params.set('fundTypes', normalizedSelectedInstrumentTypes.join(','));
    } else {
      params.set('excludeTreasury', '1');
    }
    if (apiRangeActive.volatility) {
      put('volatilityMin', volatilityMinInput);
      put('volatilityMax', volatilityMaxInput);
    }
    if (apiRangeActive.currentPvp) {
      put('currentPvpMin', currentPvpMinInput);
      put('currentPvpMax', currentPvpMaxInput);
    }
    if (apiRangeActive.dy12m) {
      put('dy12mMin', dyMesMinInput);
      put('dy12mMax', dyMesMaxInput);
    }
    if (apiRangeActive.inadimplencia) {
      put('inadimplenciaMin', inadimplenciaMinInput);
      put('inadimplenciaMax', inadimplenciaMaxInput);
    }
    if (apiRangeActive.vacancia) {
      put('vacanciaMin', vacanciaMinInput);
      put('vacanciaMax', vacanciaMaxInput);
    }
    if (apiRangeActive.cashPressureMonths12m) {
      put('cashPressureMonths12mMin', cashPressureMonths12mMinInput);
      put('cashPressureMonths12mMax', cashPressureMonths12mMaxInput);
    }
    if (apiRangeActive.concentracaoTijolo) {
      put('concentracaoTijoloMin', concentracaoTijoloMinInput);
      put('concentracaoTijoloMax', concentracaoTijoloMaxInput);
    }
    if (apiRangeActive.concentracaoPapel) {
      put('concentracaoPapelMin', concentracaoPapelMinInput);
      put('concentracaoPapelMax', concentracaoPapelMaxInput);
    }
    if (apiRangeActive.liquidezDiaria30d) {
      put('liquidezDiaria30dMin', liquidezDiaria30dMinInput);
      put('liquidezDiaria30dMax', liquidezDiaria30dMaxInput);
    }
    if (apiRangeActive.selicSensitivityScore) {
      put('selicSensitivityScoreMin', selicSensitivityScoreMinInput);
      put('selicSensitivityScoreMax', selicSensitivityScoreMaxInput);
    }
    if (apiRangeActive.leverageIndexScore) {
      put('leverageIndexScoreMin', leverageIndexScoreMinInput);
      put('leverageIndexScoreMax', leverageIndexScoreMaxInput);
    }
    if (apiRangeActive.distanceTo12mHigh) {
      put('distanceTo12mHighMin', distanceTo12mHighMinInput);
      put('distanceTo12mHighMax', distanceTo12mHighMaxInput);
    }
    if (apiRangeActive.vpGrowth12m) {
      put('vpPerShareGrowth12mMin', vpGrowth12mMinInput);
      put('vpPerShareGrowth12mMax', vpGrowth12mMaxInput);
    }
    if (apiRangeActive.patrimonio) {
      put('patrimonioMin', patrimonioMinInput);
      put('patrimonioMax', patrimonioMaxInput);
    }
    if (apiRangeActive.cotistas) {
      put('cotistasMin', cotistasMinInput);
      put('cotistasMax', cotistasMaxInput);
    }

    put('classificacao', classificacaoFilter);
    put('segmento', segmentoFilter);
    put('cashTrendStatus', cashTrendStatusFilter);
    return params.toString();
  }, [
    apiRangeActive,
    cashPressureMonths12mMaxInput,
    cashPressureMonths12mMinInput,
    cashTrendStatusFilter,
    classificacaoFilter,
    concentracaoPapelMaxInput,
    concentracaoPapelMinInput,
    concentracaoTijoloMaxInput,
    concentracaoTijoloMinInput,
    cotistasMaxInput,
    cotistasMinInput,
    currentPvpMaxInput,
    currentPvpMinInput,
    distanceTo12mHighMaxInput,
    distanceTo12mHighMinInput,
    dyMesMaxInput,
    dyMesMinInput,
    inadimplenciaMaxInput,
    inadimplenciaMinInput,
    liquidezDiaria30dMaxInput,
    liquidezDiaria30dMinInput,
    leverageIndexScoreMaxInput,
    leverageIndexScoreMinInput,
    normalizedSelectedInstrumentTypes,
    patrimonioMaxInput,
    patrimonioMinInput,
    selicSensitivityScoreMaxInput,
    selicSensitivityScoreMinInput,
    segmentoFilter,
    vacanciaMaxInput,
    vacanciaMinInput,
    volatilityMaxInput,
    volatilityMinInput,
    vpGrowth12mMaxInput,
    vpGrowth12mMinInput,
  ]);

  const sortQueryValue = useMemo(() => {
    if (sortColumns.length === 0) return '';
    return sortColumns.map((item) => `${item.field}:${item.direction === 'Descending' ? 'desc' : 'asc'}`).join(',');
  }, [sortColumns]);

  const visibleColumnFields = useMemo(() => {
    const hidden = new Set(hiddenColumnFields);
    return columnChooserOptions.map((option) => option.field).filter((field) => !hidden.has(field));
  }, [columnChooserOptions, hiddenColumnFields]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!hasHydratedFromUrlRef.current) return;
    if (!hasSkippedInitialUrlCompactionRef.current) {
      hasSkippedInitialUrlCompactionRef.current = true;
      return;
    }
    const legacyParams = new URLSearchParams(apiFilterQueryString);
    const filters: Record<string, string> = {};
    legacyParams.forEach((value, key) => {
      filters[key] = value;
    });
    const compactParams = buildCatalogoCompactQueryParams({
      filters,
      sortColumns,
      visibleColumns: visibleColumnFields,
    });
    const nextParams = compactParams;
    const nextQueryString = nextParams.toString();
    const current = new URLSearchParams(window.location.search).toString();
    if (current === nextQueryString) return;
    const pathname = window.location.pathname;
    const hash = window.location.hash ?? '';
    const nextUrl = nextQueryString ? `${pathname}?${nextQueryString}${hash}` : `${pathname}${hash}`;
    window.history.replaceState(null, '', nextUrl);
  }, [apiFilterQueryString, sortColumns, sortQueryValue, visibleColumnFields]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const path = window.location.pathname;
    const queryString = window.location.search.startsWith('?')
      ? window.location.search.slice(1)
      : window.location.search;

    const defaultMatch =
      defaultSearches.find((row) => row.catalogPath === path && row.queryString === queryString) ?? null;
    if (defaultMatch) {
      setSelectedDefaultSearchId(defaultMatch.id);
    }

    if (!isAuthenticated) {
      setSelectedSavedSearchId('');
      return;
    }
    const savedMatch = savedSearches.find((row) => row.catalogPath === path && row.queryString === queryString) ?? null;
    if (savedMatch) {
      setSelectedSavedSearchId(savedMatch.id);
    }
  }, [defaultSearches, isAuthenticated, savedSearches, apiFilterQueryString, sortQueryValue]);

  useEffect(() => {
    setHasLoadedApiFilters(false);
    setApiFilterState({ loading: false, error: null });
  }, [baseQueryString]);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    const loadBaseInstruments = async () => {
      setBaseInstrumentState({ loading: true, error: null });
      try {
        const qs = baseQueryString ? `?${baseQueryString}` : '';
        const res = await fetch(`/api/catalogo/tickers${qs}`, {
          signal: controller.signal,
          cache: 'no-store',
        });
        if (!res.ok) {
          throw new Error(`Erro ao carregar tickers (${res.status})`);
        }
        const data = (await res.json()) as InstrumentRow[];
        if (!cancelled) {
          setBaseInstruments(data);
          setBaseInstrumentState({ loading: false, error: null });
        }
      } catch (err: any) {
        if (!cancelled && err?.name !== 'AbortError') {
          setBaseInstruments([]);
          setBaseInstrumentState({
            loading: false,
            error: err?.message ?? 'Falha ao carregar tickers',
          });
        }
      }
    };

    loadBaseInstruments();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [baseQueryString]);

  const hasCategoricalQueryFilters = useMemo(
    () => Boolean(classificacaoFilter.trim() || segmentoFilter.trim() || cashTrendStatusFilter.trim()),
    [cashTrendStatusFilter, classificacaoFilter, segmentoFilter],
  );

  const hasAdvancedQueryFilters = useMemo(
    () =>
      hasCategoricalQueryFilters ||
      isRangeFilterActive(currentPvpMinInput, currentPvpMaxInput, sliderRanges.currentPvp) ||
      isRangeFilterActive(dyMesMinInput, dyMesMaxInput, sliderRanges.dyMes) ||
      isRangeFilterActive(inadimplenciaMinInput, inadimplenciaMaxInput, sliderRanges.inadimplencia) ||
      isRangeFilterActive(vacanciaMinInput, vacanciaMaxInput, sliderRanges.vacancia) ||
      isRangeFilterActive(patrimonioMinInput, patrimonioMaxInput, sliderRanges.patrimonio, { integer: true }) ||
      isRangeFilterActive(cotistasMinInput, cotistasMaxInput, sliderRanges.cotistas, { integer: true }) ||
      isRangeFilterActive(volatilityMinInput, volatilityMaxInput, sliderRanges.volatility) ||
      isRangeFilterActive(distanceTo12mHighMinInput, distanceTo12mHighMaxInput, sliderRanges.distanceTo12mHigh) ||
      isRangeFilterActive(
        cashPressureMonths12mMinInput,
        cashPressureMonths12mMaxInput,
        sliderRanges.cashPressureMonths12m,
        { integer: true },
      ) ||
      isRangeFilterActive(concentracaoTijoloMinInput, concentracaoTijoloMaxInput, sliderRanges.concentracaoTijolo) ||
      isRangeFilterActive(concentracaoPapelMinInput, concentracaoPapelMaxInput, sliderRanges.concentracaoPapel) ||
      isRangeFilterActive(liquidezDiaria30dMinInput, liquidezDiaria30dMaxInput, sliderRanges.liquidezDiaria30d, {
        integer: true,
      }) ||
      isRangeFilterActive(
        selicSensitivityScoreMinInput,
        selicSensitivityScoreMaxInput,
        sliderRanges.selicSensitivityScore,
      ) ||
      isRangeFilterActive(leverageIndexScoreMinInput, leverageIndexScoreMaxInput, sliderRanges.leverageIndexScore) ||
      isRangeFilterActive(vpGrowth12mMinInput, vpGrowth12mMaxInput, sliderRanges.vpGrowth12m),
    [
      cashPressureMonths12mMaxInput,
      cashPressureMonths12mMinInput,
      concentracaoPapelMaxInput,
      concentracaoPapelMinInput,
      concentracaoTijoloMaxInput,
      concentracaoTijoloMinInput,
      cotistasMaxInput,
      cotistasMinInput,
      currentPvpMaxInput,
      currentPvpMinInput,
      distanceTo12mHighMaxInput,
      distanceTo12mHighMinInput,
      dyMesMaxInput,
      dyMesMinInput,
      hasCategoricalQueryFilters,
      inadimplenciaMaxInput,
      inadimplenciaMinInput,
      leverageIndexScoreMaxInput,
      leverageIndexScoreMinInput,
      liquidezDiaria30dMaxInput,
      liquidezDiaria30dMinInput,
      patrimonioMaxInput,
      patrimonioMinInput,
      selicSensitivityScoreMaxInput,
      selicSensitivityScoreMinInput,
      sliderRanges,
      vacanciaMaxInput,
      vacanciaMinInput,
      volatilityMaxInput,
      volatilityMinInput,
      vpGrowth12mMaxInput,
      vpGrowth12mMinInput,
    ],
  );

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    const loadFilteredInstruments = async () => {
      if (baseInstrumentState.loading || baseInstrumentState.error) return;
      if (!hasAdvancedQueryFilters) {
        setApiFilteredInstruments(baseInstruments);
        setHasLoadedApiFilters(true);
        setApiFilterState({ loading: false, error: null });
        return;
      }

      setApiFilterState({ loading: true, error: null });
      try {
        const qs = apiFilterQueryString ? `?${apiFilterQueryString}` : '';
        const res = await fetch(`/api/catalogo/tickers${qs}`, {
          signal: controller.signal,
          cache: 'no-store',
        });
        if (!res.ok) {
          throw new Error(`Erro ao filtrar tickers (${res.status})`);
        }
        const data = (await res.json()) as InstrumentRow[];
        if (!cancelled) {
          setApiFilteredInstruments(data);
          setHasLoadedApiFilters(true);
          setApiFilterState({ loading: false, error: null });
        }
      } catch (err: any) {
        if (!cancelled && err?.name !== 'AbortError') {
          setHasLoadedApiFilters(true);
          setApiFilterState({
            loading: false,
            error: err?.message ?? 'Falha ao filtrar tickers',
          });
        }
      }
    };

    loadFilteredInstruments();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [
    apiFilterQueryString,
    baseInstrumentState.error,
    baseInstrumentState.loading,
    baseInstruments,
    hasAdvancedQueryFilters,
  ]);

  const filteredInstruments = useMemo(() => {
    if (!hasLoadedApiFilters && !apiFilterState.error) {
      return baseInstruments;
    }
    return apiFilterState.error ? [] : apiFilteredInstruments;
  }, [apiFilteredInstruments, apiFilterState.error, baseInstruments, hasLoadedApiFilters]);

  const resultsOrderSummary = useMemo(() => {
    const total = filteredInstruments.length;
    return `${total} ${total === 1 ? 'resultado' : 'resultados'}`;
  }, [filteredInstruments.length]);

  const gridDataKey = useMemo(
    () =>
      [
        baseQueryString,
        apiFilterQueryString,
        filteredInstruments.length,
        filteredInstruments[0]?.idCem ?? '',
        filteredInstruments[filteredInstruments.length - 1]?.idCem ?? '',
      ].join('|'),
    [apiFilterQueryString, baseQueryString, filteredInstruments],
  );

  const activeSortChips = useMemo(() => {
    const effectiveSortColumns = sortColumns.length > 0 ? sortColumns : DEFAULT_SORT_COLUMNS;
    return effectiveSortColumns.map((column) => {
      const label = COLUMN_CHOOSER_FULL_LABELS[column.field] ?? column.field;
      const directionLabel = column.direction === 'Ascending' ? '↑' : '↓';
      return {
        field: column.field,
        label: `${label} ${directionLabel}`,
      };
    });
  }, [sortColumns]);

  const hasMultiSort = activeSortChips.length > 1;

  const clearAllRangeInputs = useCallback(() => {
    setVolatilityMinInput('');
    setVolatilityMaxInput('');
    setCurrentPvpMinInput('');
    setCurrentPvpMaxInput('');
    setDyMesMinInput('');
    setDyMesMaxInput('');
    setInadimplenciaMinInput('');
    setInadimplenciaMaxInput('');
    setVacanciaMinInput('');
    setVacanciaMaxInput('');
    setCashPressureMonths12mMinInput('');
    setCashPressureMonths12mMaxInput('');
    setConcentracaoTijoloMinInput('');
    setConcentracaoTijoloMaxInput('');
    setConcentracaoPapelMinInput('');
    setConcentracaoPapelMaxInput('');
    setLiquidezDiaria30dMinInput('');
    setLiquidezDiaria30dMaxInput('');
    setSelicSensitivityScoreMinInput('');
    setSelicSensitivityScoreMaxInput('');
    setLeverageIndexScoreMinInput('');
    setLeverageIndexScoreMaxInput('');
    setDistanceTo12mHighMinInput('');
    setDistanceTo12mHighMaxInput('');
    setVpGrowth12mMinInput('');
    setVpGrowth12mMaxInput('');
    setPatrimonioMinInput('');
    setPatrimonioMaxInput('');
    setCotistasMinInput('');
    setCotistasMaxInput('');
  }, []);

  const clearAllFilters = useCallback(() => {
    setSelectedInstrumentTypes(initialSelection);
    setSelectedDefaultSearchId('');
    setSelectedSavedSearchId('');
    setClassificacaoFilter('');
    setSegmentoFilter('');
    setCashTrendStatusFilter('');
    setSortColumns(DEFAULT_SORT_COLUMNS);
    setShowMoreFilters(false);
    clearAllRangeInputs();
    setHasLoadedApiFilters(false);
    setApiFilterState({ loading: false, error: null });
  }, [clearAllRangeInputs, initialSelection]);

  const openSaveAsDialog = useCallback(() => {
    if (!isAuthenticated) {
      setLoginRequiredDialogOpen(true);
      return;
    }
    const loadedSearchName = selectedSavedSearch?.name ?? selectedDefaultSearch?.name ?? '';
    const loadedSearchDescription = selectedSavedSearch?.description ?? selectedDefaultSearch?.description ?? '';
    setSaveSearchName(loadedSearchName);
    setSaveSearchDescription(loadedSearchDescription);
    setSaveSearchError(null);
    setSaveSearchDialogOpen(true);
  }, [
    isAuthenticated,
    selectedDefaultSearch?.description,
    selectedDefaultSearch?.name,
    selectedSavedSearch?.description,
    selectedSavedSearch?.name,
  ]);

  const saveLoadedSearch = useCallback(async () => {
    if (!isAuthenticated) {
      setLoginRequiredDialogOpen(true);
      return;
    }
    const loadedSaved = savedSearches.find((row) => row.id === selectedSavedSearchId);
    if (!loadedSaved) {
      setSavedSearchActionMessage("Nenhuma busca personalizada carregada. Use 'Salvar como' para criar uma nova.");
      return;
    }
    if (saveSearchBusy) return;
    setSaveSearchBusy(true);
    setSavedSearchActionMessage(null);
    try {
      const { catalogPath, queryString } = getCurrentCatalogLocation();
      const res = await fetch(`/api/catalogo/saved-searches/${loadedSaved.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: loadedSaved.name,
          description: loadedSaved.description ?? '',
          catalogPath,
          queryString,
        }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as {
          error?: string;
        } | null;
        throw new Error(body?.error ?? `Erro ao sobrescrever busca (${res.status})`);
      }
      const data = (await res.json()) as CatalogSavedSearch;
      await loadSavedSearches();
      setSelectedSavedSearchId(data.id);
      setSelectedDefaultSearchId('');
      setSavedSearchActionMessage(`Busca "${data.name}" atualizada.`);
    } catch (err: any) {
      setSavedSearchActionMessage(err?.message ?? 'Falha ao sobrescrever busca');
    } finally {
      setSaveSearchBusy(false);
    }
  }, [
    getCurrentCatalogLocation,
    isAuthenticated,
    loadSavedSearches,
    saveSearchBusy,
    savedSearches,
    selectedSavedSearchId,
  ]);

  const submitSaveAsDialog = useCallback(async () => {
    if (!isAuthenticated || saveSearchBusy) return;
    const description = saveSearchDescription.trim();
    const name = saveSearchName.trim();

    if (!name) {
      setSaveSearchError('Informe um nome para a nova busca.');
      return;
    }
    setSaveSearchBusy(true);
    setSaveSearchError(null);
    setSavedSearchActionMessage(null);
    try {
      const { catalogPath, queryString } = getCurrentCatalogLocation();
      const payload = {
        name,
        description,
        catalogPath,
        queryString,
      };
      const res = await fetch('/api/catalogo/saved-searches', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as {
          error?: string;
        } | null;
        throw new Error(body?.error ?? `Erro ao salvar busca (${res.status})`);
      }
      const data = (await res.json()) as CatalogSavedSearch;
      await loadSavedSearches();
      setSelectedSavedSearchId(data.id);
      setSelectedDefaultSearchId('');
      setSaveSearchDialogOpen(false);
      setSavedSearchActionMessage('Busca salva com sucesso.');
      applySavedSearch(data);
    } catch (err: any) {
      setSaveSearchError(err?.message ?? 'Falha ao salvar busca');
    } finally {
      setSaveSearchBusy(false);
    }
  }, [
    applySavedSearch,
    getCurrentCatalogLocation,
    isAuthenticated,
    loadSavedSearches,
    saveSearchBusy,
    saveSearchDescription,
    saveSearchName,
  ]);

  const handleCopyCurrentUrl = useCallback(async () => {
    if (typeof window === 'undefined') return;
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      setSavedSearchActionMessage('URL atual copiada.');
    } catch {
      setSavedSearchActionMessage('Não foi possível copiar a URL.');
    }
  }, []);

  const handleLoadSearch = useCallback(
    (search: { catalogPath: string; queryString: string; id: string }, kind: 'default' | 'saved') => {
      if (kind === 'default') {
        setSelectedDefaultSearchId(search.id);
        setSelectedSavedSearchId('');
      } else {
        setSelectedSavedSearchId(search.id);
        setSelectedDefaultSearchId('');
      }
      setLoadSearchDialogOpen(false);
      applySavedSearch(search);
    },
    [applySavedSearch],
  );

  const resetRangeToDefault = useCallback(
    (
      range: { min: number; max: number } | null,
      setMin: (value: string) => void,
      setMax: (value: string) => void,
      step = 0.01,
    ) => {
      if (!range) {
        setMin('');
        setMax('');
        return;
      }
      setMin(formatRangeBoundValue(range.min, { step, kind: 'min' }));
      setMax(formatRangeBoundValue(range.max, { step, kind: 'max' }));
    },
    [],
  );

  const formatActiveRangeLabel = useCallback(
    (label: string, minInput: string, maxInput: string, suffix = '', step = 0.01, integer = false) => {
      const { min, max } = integer ? parseRangeInputInteger(minInput, maxInput) : parseRangeInput(minInput, maxInput);
      const minText = min === null ? 'min livre' : formatRangeBoundValue(min, { step, kind: 'min' });
      const maxText = max === null ? 'max livre' : formatRangeBoundValue(max, { step, kind: 'max' });
      return `${label}: ${minText}${suffix} - ${maxText}${suffix}`;
    },
    [],
  );

  const activeRangeFlags = useMemo(
    () => ({
      currentPvp: isRangeFilterActive(currentPvpMinInput, currentPvpMaxInput, sliderRanges.currentPvp),
      dy12m: isRangeFilterActive(dyMesMinInput, dyMesMaxInput, sliderRanges.dyMes),
      inadimplencia: isRangeFilterActive(inadimplenciaMinInput, inadimplenciaMaxInput, sliderRanges.inadimplencia),
      vacancia: isRangeFilterActive(vacanciaMinInput, vacanciaMaxInput, sliderRanges.vacancia),
      patrimonio: isRangeFilterActive(patrimonioMinInput, patrimonioMaxInput, sliderRanges.patrimonio, {
        integer: true,
      }),
      cotistas: isRangeFilterActive(cotistasMinInput, cotistasMaxInput, sliderRanges.cotistas, { integer: true }),
      volatility: isRangeFilterActive(volatilityMinInput, volatilityMaxInput, sliderRanges.volatility),
      distanceTo12mHigh: isRangeFilterActive(
        distanceTo12mHighMinInput,
        distanceTo12mHighMaxInput,
        sliderRanges.distanceTo12mHigh,
      ),
      cashPressureMonths12m: isRangeFilterActive(
        cashPressureMonths12mMinInput,
        cashPressureMonths12mMaxInput,
        sliderRanges.cashPressureMonths12m,
        { integer: true },
      ),
      concentracaoTijolo: isRangeFilterActive(
        concentracaoTijoloMinInput,
        concentracaoTijoloMaxInput,
        sliderRanges.concentracaoTijolo,
      ),
      concentracaoPapel: isRangeFilterActive(
        concentracaoPapelMinInput,
        concentracaoPapelMaxInput,
        sliderRanges.concentracaoPapel,
      ),
      liquidezDiaria30d: isRangeFilterActive(
        liquidezDiaria30dMinInput,
        liquidezDiaria30dMaxInput,
        sliderRanges.liquidezDiaria30d,
        { integer: true },
      ),
      selicSensitivityScore: isRangeFilterActive(
        selicSensitivityScoreMinInput,
        selicSensitivityScoreMaxInput,
        sliderRanges.selicSensitivityScore,
      ),
      leverageIndexScore: isRangeFilterActive(
        leverageIndexScoreMinInput,
        leverageIndexScoreMaxInput,
        sliderRanges.leverageIndexScore,
      ),
      vpGrowth12m: isRangeFilterActive(vpGrowth12mMinInput, vpGrowth12mMaxInput, sliderRanges.vpGrowth12m),
    }),
    [
      cashPressureMonths12mMaxInput,
      cashPressureMonths12mMinInput,
      concentracaoPapelMaxInput,
      concentracaoPapelMinInput,
      concentracaoTijoloMaxInput,
      concentracaoTijoloMinInput,
      cotistasMaxInput,
      cotistasMinInput,
      currentPvpMaxInput,
      currentPvpMinInput,
      distanceTo12mHighMaxInput,
      distanceTo12mHighMinInput,
      dyMesMaxInput,
      dyMesMinInput,
      inadimplenciaMaxInput,
      inadimplenciaMinInput,
      leverageIndexScoreMaxInput,
      leverageIndexScoreMinInput,
      liquidezDiaria30dMaxInput,
      liquidezDiaria30dMinInput,
      patrimonioMaxInput,
      patrimonioMinInput,
      selicSensitivityScoreMaxInput,
      selicSensitivityScoreMinInput,
      sliderRanges,
      vacanciaMaxInput,
      vacanciaMinInput,
      volatilityMaxInput,
      volatilityMinInput,
      vpGrowth12mMaxInput,
      vpGrowth12mMinInput,
    ],
  );

  const activeFilterChips = useMemo(() => {
    const chips: Array<{ key: string; label: string; onDelete: () => void }> = [];

    if (classificacaoFilter) {
      chips.push({
        key: 'classificacao',
        label: `Classificação: ${classificacaoLabelByValue.get(classificacaoFilter) ?? classificacaoFilter}`,
        onDelete: () => setClassificacaoFilter(''),
      });
    }
    if (segmentoFilter) {
      chips.push({
        key: 'segmento',
        label: `Segmento: ${segmentoFilter}`,
        onDelete: () => setSegmentoFilter(''),
      });
    }
    if (cashTrendStatusFilter) {
      chips.push({
        key: 'cashTrendStatus',
        label: `Caixa: ${cashTrendLabelByValue.get(cashTrendStatusFilter) ?? cashTrendStatusFilter}`,
        onDelete: () => setCashTrendStatusFilter(''),
      });
    }

    if (activeRangeFlags.currentPvp) {
      chips.push({
        key: 'currentPvp',
        label: formatActiveRangeLabel('P/VP', currentPvpMinInput, currentPvpMaxInput),
        onDelete: () => resetRangeToDefault(sliderRanges.currentPvp, setCurrentPvpMinInput, setCurrentPvpMaxInput),
      });
    }
    if (activeRangeFlags.dy12m) {
      chips.push({
        key: 'dy12m',
        label: formatActiveRangeLabel('DY 12m', dyMesMinInput, dyMesMaxInput, '%'),
        onDelete: () => resetRangeToDefault(sliderRanges.dyMes, setDyMesMinInput, setDyMesMaxInput),
      });
    }
    if (activeRangeFlags.inadimplencia) {
      chips.push({
        key: 'inadimplencia',
        label: formatActiveRangeLabel('Inadimplência', inadimplenciaMinInput, inadimplenciaMaxInput, '%'),
        onDelete: () =>
          resetRangeToDefault(sliderRanges.inadimplencia, setInadimplenciaMinInput, setInadimplenciaMaxInput),
      });
    }
    if (activeRangeFlags.vacancia) {
      chips.push({
        key: 'vacancia',
        label: formatActiveRangeLabel('Vacância', vacanciaMinInput, vacanciaMaxInput, '%'),
        onDelete: () => resetRangeToDefault(sliderRanges.vacancia, setVacanciaMinInput, setVacanciaMaxInput),
      });
    }
    if (activeRangeFlags.patrimonio) {
      chips.push({
        key: 'patrimonio',
        label: formatActiveRangeLabel('Patrimônio', patrimonioMinInput, patrimonioMaxInput, '', 1, true),
        onDelete: () => resetRangeToDefault(sliderRanges.patrimonio, setPatrimonioMinInput, setPatrimonioMaxInput, 1),
      });
    }
    if (activeRangeFlags.cotistas) {
      chips.push({
        key: 'cotistas',
        label: formatActiveRangeLabel('Cotistas', cotistasMinInput, cotistasMaxInput, '', 1, true),
        onDelete: () => resetRangeToDefault(sliderRanges.cotistas, setCotistasMinInput, setCotistasMaxInput, 1),
      });
    }
    if (activeRangeFlags.volatility) {
      chips.push({
        key: 'volatility',
        label: formatActiveRangeLabel('Volatilidade', volatilityMinInput, volatilityMaxInput, '%'),
        onDelete: () => resetRangeToDefault(sliderRanges.volatility, setVolatilityMinInput, setVolatilityMaxInput),
      });
    }
    if (activeRangeFlags.distanceTo12mHigh) {
      chips.push({
        key: 'distanceTo12mHigh',
        label: formatActiveRangeLabel('Dist. 12m', distanceTo12mHighMinInput, distanceTo12mHighMaxInput, '%'),
        onDelete: () =>
          resetRangeToDefault(
            sliderRanges.distanceTo12mHigh,
            setDistanceTo12mHighMinInput,
            setDistanceTo12mHighMaxInput,
          ),
      });
    }
    if (activeRangeFlags.cashPressureMonths12m) {
      chips.push({
        key: 'cashPressureMonths12m',
        label: formatActiveRangeLabel(
          'Pressão 12m',
          cashPressureMonths12mMinInput,
          cashPressureMonths12mMaxInput,
          '',
          1,
          true,
        ),
        onDelete: () =>
          resetRangeToDefault(
            sliderRanges.cashPressureMonths12m,
            setCashPressureMonths12mMinInput,
            setCashPressureMonths12mMaxInput,
            1,
          ),
      });
    }
    if (activeRangeFlags.concentracaoTijolo) {
      chips.push({
        key: 'concentracaoTijolo',
        label: formatActiveRangeLabel('Conc. Tijolo', concentracaoTijoloMinInput, concentracaoTijoloMaxInput, '%'),
        onDelete: () =>
          resetRangeToDefault(
            sliderRanges.concentracaoTijolo,
            setConcentracaoTijoloMinInput,
            setConcentracaoTijoloMaxInput,
          ),
      });
    }
    if (activeRangeFlags.concentracaoPapel) {
      chips.push({
        key: 'concentracaoPapel',
        label: formatActiveRangeLabel('Conc. Papel', concentracaoPapelMinInput, concentracaoPapelMaxInput, '%'),
        onDelete: () =>
          resetRangeToDefault(
            sliderRanges.concentracaoPapel,
            setConcentracaoPapelMinInput,
            setConcentracaoPapelMaxInput,
          ),
      });
    }
    if (activeRangeFlags.liquidezDiaria30d) {
      chips.push({
        key: 'liquidezDiaria30d',
        label: formatActiveRangeLabel(
          'Liq. diária 30d',
          liquidezDiaria30dMinInput,
          liquidezDiaria30dMaxInput,
          '',
          1,
          true,
        ),
        onDelete: () =>
          resetRangeToDefault(
            sliderRanges.liquidezDiaria30d,
            setLiquidezDiaria30dMinInput,
            setLiquidezDiaria30dMaxInput,
            1,
          ),
      });
    }
    if (activeRangeFlags.selicSensitivityScore) {
      chips.push({
        key: 'selicSensitivityScore',
        label: formatActiveRangeLabel('Sens. Selic', selicSensitivityScoreMinInput, selicSensitivityScoreMaxInput, '%'),
        onDelete: () =>
          resetRangeToDefault(
            sliderRanges.selicSensitivityScore,
            setSelicSensitivityScoreMinInput,
            setSelicSensitivityScoreMaxInput,
          ),
      });
    }
    if (activeRangeFlags.leverageIndexScore) {
      chips.push({
        key: 'leverageIndexScore',
        label: formatActiveRangeLabel('Alavancagem', leverageIndexScoreMinInput, leverageIndexScoreMaxInput, '%'),
        onDelete: () =>
          resetRangeToDefault(
            sliderRanges.leverageIndexScore,
            setLeverageIndexScoreMinInput,
            setLeverageIndexScoreMaxInput,
          ),
      });
    }
    if (activeRangeFlags.vpGrowth12m) {
      chips.push({
        key: 'vpGrowth12m',
        label: formatActiveRangeLabel('Cresc. VP/cota', vpGrowth12mMinInput, vpGrowth12mMaxInput, '%'),
        onDelete: () => resetRangeToDefault(sliderRanges.vpGrowth12m, setVpGrowth12mMinInput, setVpGrowth12mMaxInput),
      });
    }

    return chips;
  }, [
    activeRangeFlags,
    cashPressureMonths12mMaxInput,
    cashPressureMonths12mMinInput,
    cashTrendLabelByValue,
    cashTrendStatusFilter,
    classificacaoFilter,
    classificacaoLabelByValue,
    concentracaoPapelMaxInput,
    concentracaoPapelMinInput,
    concentracaoTijoloMaxInput,
    concentracaoTijoloMinInput,
    cotistasMaxInput,
    cotistasMinInput,
    currentPvpMaxInput,
    currentPvpMinInput,
    distanceTo12mHighMaxInput,
    distanceTo12mHighMinInput,
    dyMesMaxInput,
    dyMesMinInput,
    formatActiveRangeLabel,
    inadimplenciaMaxInput,
    inadimplenciaMinInput,
    leverageIndexScoreMaxInput,
    leverageIndexScoreMinInput,
    liquidezDiaria30dMaxInput,
    liquidezDiaria30dMinInput,
    patrimonioMaxInput,
    patrimonioMinInput,
    resetRangeToDefault,
    selicSensitivityScoreMaxInput,
    selicSensitivityScoreMinInput,
    segmentoFilter,
    sliderRanges,
    vacanciaMaxInput,
    vacanciaMinInput,
    volatilityMaxInput,
    volatilityMinInput,
    vpGrowth12mMaxInput,
    vpGrowth12mMinInput,
  ]);

  const hasHiddenActiveFilters = useMemo(() => {
    return (
      activeRangeFlags.inadimplencia ||
      activeRangeFlags.patrimonio ||
      activeRangeFlags.volatility ||
      activeRangeFlags.distanceTo12mHigh ||
      activeRangeFlags.cashPressureMonths12m ||
      activeRangeFlags.concentracaoTijolo ||
      activeRangeFlags.concentracaoPapel ||
      activeRangeFlags.liquidezDiaria30d ||
      activeRangeFlags.selicSensitivityScore ||
      activeRangeFlags.leverageIndexScore ||
      activeRangeFlags.vpGrowth12m
    );
  }, [activeRangeFlags]);

  const classificacaoOptions = useMemo(
    () =>
      Array.from(classificacaoLabelByValue.entries())
        .sort((a, b) => a[1].localeCompare(b[1], 'pt-BR'))
        .map(([value, label]) => ({ value, label })),
    [classificacaoLabelByValue],
  );

  const segmentoOptions = useMemo(
    () =>
      Array.from(new Set(optionsBaseRows.map((ticker) => (ticker.segmento ?? '').trim()).filter(Boolean))).sort(
        (a, b) => a.localeCompare(b, 'pt-BR'),
      ),
    [optionsBaseRows],
  );

  const cashTrendStatusOptions = useMemo(
    () =>
      Array.from(cashTrendLabelByValue.entries())
        .sort((a, b) => a[1].localeCompare(b[1], 'pt-BR'))
        .map(([value, label]) => ({ value, label })),
    [cashTrendLabelByValue],
  );

  if (baseInstrumentState.loading && baseInstruments.length === 0) {
    return (
      <Paper variant='outlined' sx={{ mt: 3, p: 3, textAlign: 'center', color: 'text.secondary' }}>
        Carregando catálogo...
      </Paper>
    );
  }

  if (baseInstrumentState.error && baseInstruments.length === 0) {
    return (
      <Typography variant='body2' color='error' sx={{ mt: 2 }}>
        {baseInstrumentState.error}
      </Typography>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Paper
        variant='outlined'
        sx={{
          p: { xs: 1.5, md: 2 },
          backgroundColor: 'hsl(var(--card))',
          borderColor: 'hsl(var(--border))',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'flex-start', md: 'center' },
            justifyContent: 'space-between',
            gap: 1,
            mb: 1.5,
          }}
        >
          <Box>
            <Typography variant='h6' fontWeight={800}>
              Catálogo de Fundos
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              Filtros por tipos e métricas
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: { xs: 'flex-start', md: 'flex-end' },
            }}
          >
            {currentLoadedSearchLabel ? (
              <Chip
                label={currentLoadedSearchLabel}
                size='small'
                sx={{ bgcolor: 'hsl(var(--muted) / 0.65)', fontWeight: 700 }}
              />
            ) : null}
            <Tooltip title='Carregar estratégia pronta ou busca personalizada' arrow>
              <Button
                size='small'
                variant='outlined'
                onClick={() => setLoadSearchDialogOpen(true)}
                startIcon={<FolderOpen size={15} />}
                sx={outlinedActionButtonSx}
              >
                Carregar
              </Button>
            </Tooltip>
            <Tooltip title='Salvar busca atual' arrow>
              <Button
                size='small'
                variant='outlined'
                onClick={saveLoadedSearch}
                startIcon={<Save size={15} />}
                sx={outlinedActionButtonSx}
              >
                Salvar
              </Button>
            </Tooltip>
            <Tooltip title='Salvar busca atual como nova' arrow>
              <Button
                size='small'
                variant='outlined'
                onClick={openSaveAsDialog}
                startIcon={<Save size={15} />}
                sx={outlinedActionButtonSx}
              >
                Salvar como
              </Button>
            </Tooltip>
            <Tooltip title='Copiar URL da busca atual' arrow>
              <Button
                size='small'
                variant='outlined'
                onClick={handleCopyCurrentUrl}
                startIcon={<Share2 size={15} />}
                sx={outlinedActionButtonSx}
              >
                Compartilhar
              </Button>
            </Tooltip>
          </Box>
        </Box>

        {fundTypeState.error ? (
          <Typography variant='body2' color='error' sx={{ mb: 1.5 }}>
            {fundTypeState.error}
          </Typography>
        ) : null}
        {apiFilterState.error ? (
          <Typography variant='body2' color='error' sx={{ mb: 1.5 }}>
            {apiFilterState.error}
          </Typography>
        ) : null}
        {savedSearchActionMessage ? (
          <Typography variant='body2' sx={{ mb: 1.2, color: 'text.secondary' }}>
            {savedSearchActionMessage}
          </Typography>
        ) : null}

        <Divider sx={{ mb: 1.5 }} />

        <Paper
          variant='outlined'
          sx={{
            mb: 1.5,
            p: { xs: 1.2, md: 1.6 },
            borderColor: 'hsl(var(--border) / 0.9)',
            background: 'linear-gradient(180deg, hsl(var(--card)) 0%, hsl(var(--muted) / 0.24) 100%)',
          }}
        >
          <Stack direction='row' alignItems='center' spacing={1} sx={{ mb: 2.5 }}>
            <Typography variant='subtitle2' fontWeight={900} sx={{ whiteSpace: 'nowrap' }}>
              Filtros
            </Typography>
            {activeFilterChips.length > 0 ? (
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Stack direction='row' spacing={0.8} useFlexGap flexWrap='wrap' justifyContent='flex-end'>
                  {activeFilterChips.map((chip) => (
                    <Chip
                      key={chip.key}
                      size='small'
                      label={chip.label}
                      onDelete={chip.onDelete}
                      sx={{
                        bgcolor: 'hsl(var(--primary) / 0.14)',
                        color: 'hsl(var(--foreground))',
                        fontWeight: 700,
                      }}
                    />
                  ))}
                </Stack>
              </Box>
            ) : null}
          </Stack>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(auto-fit, minmax(220px, 1fr))',
              },
              gap: 1.2,
              mb: 1.2,
              width: '100%',
            }}
          >
            <TextField
              size='small'
              label='Tipo de fundo'
              InputLabelProps={{ shrink: true }}
              value={fundTypeDisplayValue}
              onClick={() => {
                if (!fundTypeState.loading && fundTypes.length > 0) {
                  openInstrumentTypeDialog();
                }
              }}
              fullWidth
              disabled={fundTypeState.loading || fundTypes.length === 0}
              InputProps={{
                readOnly: true,
              }}
              sx={{
                '& .MuiInputBase-root': { cursor: 'pointer' },
                '& .MuiInputBase-input': {
                  cursor: 'pointer',
                  fontWeight: 700,
                  color: normalizedSelectedInstrumentTypes.length > 0 ? activeFilterColor : 'inherit',
                },
                ...(normalizedSelectedInstrumentTypes.length > 0
                  ? {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: activeFilterColor,
                      },
                    }
                  : {}),
              }}
            />
            {/** Filtros categóricos */}
            <TextField
              select
              size='small'
              label={
                <FilterLabel
                  label='Classificação'
                  help='Perfil CVM derivado da carteira. "SEM DADOS" inclui valores nulos e em branco.'
                />
              }
              value={classificacaoFilter}
              onChange={(event) => setClassificacaoFilter(event.target.value)}
              fullWidth
              sx={
                classificacaoFilter
                  ? {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: activeFilterColor,
                      },
                    }
                  : undefined
              }
            >
              <MenuItem value=''>Todas</MenuItem>
              {classificacaoOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size='small'
              label={
                <FilterLabel
                  label='Segmento'
                  help='Segmento de atuação informado no informe mensal CVM (última competência).'
                />
              }
              value={segmentoFilter}
              onChange={(event) => setSegmentoFilter(event.target.value)}
              fullWidth
              sx={
                segmentoFilter
                  ? {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: activeFilterColor,
                      },
                    }
                  : undefined
              }
            >
              <MenuItem value=''>Todos</MenuItem>
              {segmentoOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size='small'
              label={
                <FilterLabel
                  label='Resultado de caixa'
                  help='Baseado no caixa líquido mensal: (disponibilidades + títulos públicos + títulos privados + fundos renda fixa + valores a receber - passivo total). Compara atual vs competência anterior: positivo/negativo com ou sem melhora.'
                />
              }
              value={cashTrendStatusFilter}
              onChange={(event) => setCashTrendStatusFilter(event.target.value)}
              fullWidth
              sx={
                cashTrendStatusFilter
                  ? {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: activeFilterColor,
                      },
                    }
                  : undefined
              }
            >
              <MenuItem value=''>Todos</MenuItem>
              {cashTrendStatusOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, minmax(0, 1fr))',
                md: 'repeat(4, minmax(0, 1fr))',
              },
              gap: 1.2,
              width: '100%',
            }}
          >
            <RangeFilterCard
              label={<FilterLabel label='DY 12m' help='Dividend yield acumulado dos últimos 12 meses.' />}
              minValue={dyMesMinInput}
              maxValue={dyMesMaxInput}
              onMinChange={setDyMesMinInput}
              onMaxChange={setDyMesMaxInput}
              suffix='%'
              sliderBounds={sliderRanges.dyMes}
              isActive={activeRangeFlags.dy12m}
            />
            <RangeFilterCard
              label={
                <FilterLabel
                  label='P/VP atual'
                  help='Preço atual dividido pelo valor patrimonial por cota mais recente.'
                />
              }
              minValue={currentPvpMinInput}
              maxValue={currentPvpMaxInput}
              onMinChange={setCurrentPvpMinInput}
              onMaxChange={setCurrentPvpMaxInput}
              sliderBounds={sliderRanges.currentPvp}
              isActive={activeRangeFlags.currentPvp}
            />
            <RangeFilterCard
              label={<FilterLabel label='Nº de cotistas' help='Número de cotistas mais recente disponível na base.' />}
              minValue={cotistasMinInput}
              maxValue={cotistasMaxInput}
              onMinChange={setCotistasMinInput}
              onMaxChange={setCotistasMaxInput}
              sliderBounds={sliderRanges.cotistas}
              sliderStep={1}
              isActive={activeRangeFlags.cotistas}
            />
            <RangeFilterCard
              label={
                <FilterLabel label='Vacância' help='Vacância imobiliária agregada da base trimestral mais recente.' />
              }
              minValue={vacanciaMinInput}
              maxValue={vacanciaMaxInput}
              onMinChange={setVacanciaMinInput}
              onMaxChange={setVacanciaMaxInput}
              suffix='%'
              sliderBounds={sliderRanges.vacancia}
              isActive={activeRangeFlags.vacancia}
            />
          </Box>

          <Box
            sx={{
              mt: showMoreFilters ? 1.2 : 0,
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, minmax(0, 1fr))',
                md: 'repeat(4, minmax(0, 1fr))',
              },
              gap: 1.2,
              width: '100%',
            }}
          >
            {showMoreFilters ? (
              <>
                <RangeFilterCard
                  label={
                    <FilterLabel
                      label='Inadimplência'
                      help='Inadimplência imobiliária agregada no último trimestre disponível.'
                    />
                  }
                  minValue={inadimplenciaMinInput}
                  maxValue={inadimplenciaMaxInput}
                  onMinChange={setInadimplenciaMinInput}
                  onMaxChange={setInadimplenciaMaxInput}
                  suffix='%'
                  sliderBounds={sliderRanges.inadimplencia}
                  isActive={activeRangeFlags.inadimplencia}
                />
                <RangeFilterCard
                  label={
                    <FilterLabel label='Patrimônio' help='Patrimônio líquido mais recente disponível para o fundo.' />
                  }
                  minValue={patrimonioMinInput}
                  maxValue={patrimonioMaxInput}
                  onMinChange={setPatrimonioMinInput}
                  onMaxChange={setPatrimonioMaxInput}
                  suffix='R$'
                  sliderBounds={sliderRanges.patrimonio}
                  sliderStep={10000}
                  isActive={activeRangeFlags.patrimonio}
                />
                <RangeFilterCard
                  label={
                    <FilterLabel
                      label='Volatilidade 21d a.a.'
                      help='Métrica de risco/oscilações de preço. Não mede “barateza” do ativo.'
                    />
                  }
                  minValue={volatilityMinInput}
                  maxValue={volatilityMaxInput}
                  onMinChange={setVolatilityMinInput}
                  onMaxChange={setVolatilityMaxInput}
                  suffix='%'
                  sliderBounds={sliderRanges.volatility}
                  isActive={activeRangeFlags.volatility}
                />
                <RangeFilterCard
                  label={
                    <FilterLabel
                      label='Dist. da máxima 12m'
                      help='Quanto o preço atual está abaixo da máxima dos últimos 12 meses. Quanto maior, mais distante do topo recente.'
                    />
                  }
                  minValue={distanceTo12mHighMinInput}
                  maxValue={distanceTo12mHighMaxInput}
                  onMinChange={setDistanceTo12mHighMinInput}
                  onMaxChange={setDistanceTo12mHighMaxInput}
                  suffix='%'
                  sliderBounds={sliderRanges.distanceTo12mHigh}
                  isActive={activeRangeFlags.distanceTo12mHigh}
                />
                <RangeFilterCard
                  label={
                    <FilterLabel
                      label='Tempo sob pressão (12m)'
                      help='Quantidade de competências mensais dos últimos 12 meses em que o caixa líquido ficou negativo.'
                    />
                  }
                  minValue={cashPressureMonths12mMinInput}
                  maxValue={cashPressureMonths12mMaxInput}
                  onMinChange={setCashPressureMonths12mMinInput}
                  onMaxChange={setCashPressureMonths12mMaxInput}
                  sliderBounds={sliderRanges.cashPressureMonths12m}
                  sliderStep={1}
                  isActive={activeRangeFlags.cashPressureMonths12m}
                />
                <RangeFilterCard
                  label={
                    <FilterLabel
                      label='Concentração Tijolo'
                      help='Maior posição do perfil Tijolo dividida pelo total do próprio perfil (metodologia da aba Resumo).'
                    />
                  }
                  minValue={concentracaoTijoloMinInput}
                  maxValue={concentracaoTijoloMaxInput}
                  onMinChange={setConcentracaoTijoloMinInput}
                  onMaxChange={setConcentracaoTijoloMaxInput}
                  suffix='%'
                  sliderBounds={sliderRanges.concentracaoTijolo}
                  isActive={activeRangeFlags.concentracaoTijolo}
                />
                <RangeFilterCard
                  label={
                    <FilterLabel
                      label='Concentração Papel'
                      help='Maior posição do perfil Papel dividida pelo total do próprio perfil (metodologia da aba Resumo).'
                    />
                  }
                  minValue={concentracaoPapelMinInput}
                  maxValue={concentracaoPapelMaxInput}
                  onMinChange={setConcentracaoPapelMinInput}
                  onMaxChange={setConcentracaoPapelMaxInput}
                  suffix='%'
                  sliderBounds={sliderRanges.concentracaoPapel}
                  isActive={activeRangeFlags.concentracaoPapel}
                />
                <RangeFilterCard
                  label={
                    <FilterLabel
                      label='Liquidez diária (30d)'
                      help='Média diária do volume financeiro negociado (preço de fechamento x volume) nos últimos 30 pregões.'
                    />
                  }
                  minValue={liquidezDiaria30dMinInput}
                  maxValue={liquidezDiaria30dMaxInput}
                  onMinChange={setLiquidezDiaria30dMinInput}
                  onMaxChange={setLiquidezDiaria30dMaxInput}
                  suffix='R$'
                  sliderBounds={sliderRanges.liquidezDiaria30d}
                  sliderStep={1}
                  sliderScale='logarithmic'
                  isActive={activeRangeFlags.liquidezDiaria30d}
                />
                <RangeFilterCard
                  label={
                    <FilterLabel
                      label='Crescimento VP/cota (12m)'
                      help='Variação percentual do VP por cota contra 12 meses atrás.'
                    />
                  }
                  minValue={vpGrowth12mMinInput}
                  maxValue={vpGrowth12mMaxInput}
                  onMinChange={setVpGrowth12mMinInput}
                  onMaxChange={setVpGrowth12mMaxInput}
                  suffix='%'
                  sliderBounds={sliderRanges.vpGrowth12m}
                  isActive={activeRangeFlags.vpGrowth12m}
                />
                <RangeFilterCard
                  label={
                    <FilterLabel
                      label='Sensibilidade à Selic'
                      help='Estimativa de reação do fundo a mudanças da Selic. Fórmula (0-100): base = % de ativos financeiros x fator sem inflação; ajuste por indexador dominante; penalização por % em fundos investidos. Quanto maior o percentual, maior a exposição a variações da taxa de juros.'
                    />
                  }
                  minValue={selicSensitivityScoreMinInput}
                  maxValue={selicSensitivityScoreMaxInput}
                  onMinChange={setSelicSensitivityScoreMinInput}
                  onMaxChange={setSelicSensitivityScoreMaxInput}
                  suffix='%'
                  sliderBounds={sliderRanges.selicSensitivityScore}
                  isActive={activeRangeFlags.selicSensitivityScore}
                />
                <RangeFilterCard
                  label={
                    <FilterLabel
                      label='Índice de alavancagem'
                      help='Indicador de pressão financeira (0-100). Fórmula: dívida líquida proxy = max(a pagar - caixa - a receber, 0); score = (0,8 x dívida líquida/patrimônio + 0,6 x stress de liquidez) x 100, limitado entre 0 e 100. Quanto maior, maior o risco de stress financeiro.'
                    />
                  }
                  minValue={leverageIndexScoreMinInput}
                  maxValue={leverageIndexScoreMaxInput}
                  onMinChange={setLeverageIndexScoreMinInput}
                  onMaxChange={setLeverageIndexScoreMaxInput}
                  suffix='%'
                  sliderBounds={sliderRanges.leverageIndexScore}
                  isActive={activeRangeFlags.leverageIndexScore}
                />
              </>
            ) : null}
          </Box>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1,
              mt: 1.2,
            }}
          >
            <Button
              size='small'
              variant='outlined'
              onClick={() => setShowMoreFilters((current) => !current)}
              sx={{
                minWidth: 132,
                textTransform: 'none',
                fontWeight: 700,
                color: 'hsl(var(--foreground))',
                borderColor: !showMoreFilters && hasHiddenActiveFilters ? activeFilterColor : 'hsl(var(--border))',
                backgroundColor: 'hsl(var(--card))',
                '&:hover': {
                  borderColor:
                    !showMoreFilters && hasHiddenActiveFilters ? activeFilterColor : 'hsl(var(--nav-bg) / 0.55)',
                  backgroundColor:
                    !showMoreFilters && hasHiddenActiveFilters ? activeFilterBg : 'hsl(var(--muted) / 0.35)',
                },
              }}
            >
              {showMoreFilters ? 'Menos filtros' : 'Mais filtros'}
            </Button>
            <Stack direction='row' spacing={1}>
              <Button
                size='small'
                variant='outlined'
                onClick={() => setColumnsDialogOpen(true)}
                sx={{
                  minWidth: 96,
                  textTransform: 'none',
                  fontWeight: 700,
                  color: 'hsl(var(--foreground))',
                  borderColor: 'hsl(var(--border))',
                  backgroundColor: 'hsl(var(--card))',
                  '&:hover': {
                    borderColor: 'hsl(var(--nav-bg) / 0.55)',
                    backgroundColor: 'hsl(var(--muted) / 0.35)',
                  },
                }}
              >
                Colunas
              </Button>
              <Button
                size='small'
                variant='contained'
                onClick={clearAllFilters}
                sx={{
                  minWidth: 128,
                  textTransform: 'none',
                  fontWeight: 800,
                  color: 'hsl(var(--nav-foreground))',
                  backgroundColor: 'hsl(var(--nav-bg))',
                  '&:hover': { backgroundColor: 'hsl(var(--nav-subtle))' },
                }}
              >
                Limpar filtros
              </Button>
            </Stack>
          </Box>
        </Paper>

        <Dialog open={loadSearchDialogOpen} onClose={() => setLoadSearchDialogOpen(false)} fullWidth maxWidth='md'>
          <DialogTitle>Carregar busca</DialogTitle>
          <DialogContent dividers sx={{ p: 2 }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: 2,
              }}
            >
              <Box>
                <Typography variant='subtitle2' fontWeight={800} sx={{ mb: 1 }}>
                  Estratégias prontas
                </Typography>
                {defaultSearchesState.loading ? (
                  <Typography variant='body2' color='text.secondary'>
                    Carregando estratégias prontas...
                  </Typography>
                ) : defaultSearchesState.error ? (
                  <Typography variant='body2' color='error'>
                    {defaultSearchesState.error}
                  </Typography>
                ) : defaultSearches.length === 0 ? (
                  <Typography variant='body2' color='text.secondary'>
                    Nenhuma estratégia pronta cadastrada.
                  </Typography>
                ) : (
                  <List disablePadding sx={{ display: 'grid', gap: 0.6 }}>
                    {defaultSearches.map((search) => (
                      <ListItemButton
                        key={search.id}
                        onClick={() => handleLoadSearch(search, 'default')}
                        sx={{
                          border: '1px solid hsl(var(--border))',
                          borderRadius: 1.5,
                          alignItems: 'flex-start',
                          '&:hover': {
                            borderColor: activeFilterColor,
                            backgroundColor: activeFilterBg,
                          },
                        }}
                      >
                        <ListItemText
                          primary={search.name}
                          secondary={search.description || 'Sem descrição'}
                          primaryTypographyProps={{ fontWeight: 700 }}
                          secondaryTypographyProps={{
                            variant: 'caption',
                            color: 'text.secondary',
                          }}
                        />
                      </ListItemButton>
                    ))}
                  </List>
                )}
              </Box>

              <Box>
                <Typography variant='subtitle2' fontWeight={800} sx={{ mb: 1 }}>
                  Minhas buscas
                </Typography>
                {!isAuthenticated ? (
                  <Alert severity='info'>Entre para salvar e carregar suas buscas.</Alert>
                ) : savedSearchesState.error ? (
                  <Typography variant='body2' color='error'>
                    {savedSearchesState.error}
                  </Typography>
                ) : savedSearchesState.loading ? (
                  <Typography variant='body2' color='text.secondary'>
                    Carregando suas buscas...
                  </Typography>
                ) : savedSearches.length === 0 ? (
                  <Typography variant='body2' color='text.secondary'>
                    Você ainda não salvou nenhuma busca.
                  </Typography>
                ) : (
                  <List disablePadding sx={{ display: 'grid', gap: 0.6 }}>
                    {savedSearches.map((search) => (
                      <ListItemButton
                        key={search.id}
                        onClick={() => handleLoadSearch(search, 'saved')}
                        sx={{
                          border: '1px solid hsl(var(--border))',
                          borderRadius: 1.5,
                          alignItems: 'flex-start',
                          '&:hover': {
                            borderColor: activeFilterColor,
                            backgroundColor: activeFilterBg,
                          },
                        }}
                      >
                        <ListItemText
                          primary={search.name}
                          secondary={search.description || 'Sem descrição'}
                          primaryTypographyProps={{ fontWeight: 700 }}
                          secondaryTypographyProps={{
                            variant: 'caption',
                            color: 'text.secondary',
                          }}
                        />
                      </ListItemButton>
                    ))}
                  </List>
                )}
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 2, py: 1.2 }}>
            <Button size='small' onClick={() => setLoadSearchDialogOpen(false)} sx={outlinedActionButtonSx}>
              Fechar
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={saveSearchDialogOpen}
          onClose={() => {
            if (saveSearchBusy) return;
            setSaveSearchDialogOpen(false);
          }}
          fullWidth
          maxWidth='sm'
        >
          <DialogTitle>Salvar busca como</DialogTitle>
          <DialogContent dividers sx={{ p: 2 }}>
            <Stack spacing={1.1}>
              <TextField
                size='small'
                label='Nome da busca'
                value={saveSearchName}
                onChange={(event) => setSaveSearchName(event.target.value)}
                fullWidth
                autoFocus
              />
              <TextField
                size='small'
                label='Descrição (opcional)'
                value={saveSearchDescription}
                onChange={(event) => setSaveSearchDescription(event.target.value)}
                fullWidth
                multiline
                minRows={2}
              />

              {saveSearchError ? (
                <Typography variant='caption' color='error'>
                  {saveSearchError}
                </Typography>
              ) : null}
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 2, py: 1.2 }}>
            <Button
              size='small'
              onClick={() => setSaveSearchDialogOpen(false)}
              disabled={saveSearchBusy}
              sx={outlinedActionButtonSx}
            >
              Cancelar
            </Button>
            <Button
              size='small'
              variant='contained'
              onClick={submitSaveAsDialog}
              disabled={saveSearchBusy}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                backgroundColor: activeFilterColor,
                '&:hover': { backgroundColor: activeFilterBgHover },
              }}
            >
              Salvar
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={loginRequiredDialogOpen}
          onClose={() => setLoginRequiredDialogOpen(false)}
          fullWidth
          maxWidth='xs'
        >
          <DialogTitle>Faça login para salvar buscas</DialogTitle>
          <DialogContent dividers sx={{ p: 2 }}>
            <Stack spacing={1.2}>
              <Typography variant='body2' color='text.secondary'>
                Para salvar e reutilizar buscas personalizadas, entre na sua conta.
              </Typography>
              <Button
                variant='outlined'
                onClick={() =>
                  signIn('google', {
                    callbackUrl: typeof window !== 'undefined' ? window.location.href : '/catalogo',
                  })
                }
                sx={outlinedActionButtonSx}
              >
                Entrar com Google
              </Button>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 2, py: 1.2 }}>
            <Button size='small' onClick={() => setLoginRequiredDialogOpen(false)} sx={outlinedActionButtonSx}>
              Fechar
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={columnsDialogOpen} onClose={() => setColumnsDialogOpen(false)} fullWidth maxWidth='md'>
          <DialogTitle>Colunas visíveis</DialogTitle>
          <DialogContent dividers sx={{ p: 2 }}>
            <Stack direction='row' spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap', rowGap: 1 }}>
              <Button
                size='small'
                variant='outlined'
                onClick={showAllColumns}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  color: appColors.actions.accentOutline.text,
                  borderColor: appColors.actions.accentOutline.border,
                  '&:hover': {
                    color: appColors.actions.accentOutline.hoverText,
                    borderColor: appColors.actions.accentOutline.hoverBorder,
                    backgroundColor: appColors.actions.accentOutline.hoverBg,
                  },
                }}
              >
                Selecionar tudo
              </Button>
              <Button
                size='small'
                variant='outlined'
                onClick={invertColumnSelection}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  color: appColors.actions.accentOutline.text,
                  borderColor: appColors.actions.accentOutline.border,
                  '&:hover': {
                    color: appColors.actions.accentOutline.hoverText,
                    borderColor: appColors.actions.accentOutline.hoverBorder,
                    backgroundColor: appColors.actions.accentOutline.hoverBg,
                  },
                }}
              >
                Inverter seleção
              </Button>
            </Stack>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'repeat(2, minmax(0, 1fr))',
                  sm: 'repeat(3, minmax(0, 1fr))',
                  md: 'repeat(4, minmax(0, 1fr))',
                },
                gap: 1,
              }}
            >
              {columnChooserOptions.map((option) => {
                const isVisible = !hiddenColumnFields.includes(option.field);
                const visibleCount = columnChooserOptions.length - hiddenColumnFields.length;
                const disableToggle = isVisible && visibleCount <= 1;
                return (
                  <Button
                    key={option.field}
                    size='small'
                    fullWidth
                    variant='outlined'
                    disabled={disableToggle}
                    onClick={() => toggleColumnVisibility(option.field)}
                    aria-pressed={isVisible}
                    sx={{
                      textTransform: 'none',
                      justifyContent: 'flex-start',
                      fontWeight: isVisible ? 700 : 600,
                      minHeight: 36,
                      color: 'hsl(var(--foreground))',
                      borderColor: isVisible ? activeFilterColor : 'hsl(var(--border))',
                      backgroundColor: isVisible ? activeFilterBg : 'hsl(var(--card))',
                      boxShadow: isVisible ? activeFilterInset : 'none',
                      '&:hover': {
                        borderColor: isVisible ? activeFilterColor : 'hsl(var(--nav-bg) / 0.45)',
                        backgroundColor: isVisible ? activeFilterBgHover : 'hsl(var(--muted) / 0.35)',
                      },
                    }}
                  >
                    {option.label}
                  </Button>
                );
              })}
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 2, py: 1.2 }}>
            <Button
              size='small'
              variant='contained'
              onClick={() => setColumnsDialogOpen(false)}
              sx={{ textTransform: 'none', fontWeight: 700 }}
            >
              Fechar
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={fundTypeDialogOpen} onClose={() => setInstrumentTypeDialogOpen(false)} fullWidth maxWidth='sm'>
          <DialogTitle>Selecionar tipo de fundo</DialogTitle>
          <DialogContent dividers sx={{ p: 0 }}>
            <List disablePadding>
              {fundTypes.map((fundType) => {
                const symbol = fundType.symbol.trim().toUpperCase();
                const description = fundType.description ?? fundType.descriptionType ?? 'Sem descrição';
                const checked = normalizedSelectedInstrumentTypes.includes(symbol);
                return (
                  <ListItemButton key={fundType.id} onClick={() => toggleSelectedInstrumentType(symbol)} dense>
                    <Checkbox edge='start' checked={checked} tabIndex={-1} />
                    <ListItemText primary={symbol} secondary={description} />
                  </ListItemButton>
                );
              })}
            </List>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'flex-end', px: 2, py: 1.2 }}>
            <Button
              size='small'
              onClick={() => setInstrumentTypeDialogOpen(false)}
              sx={{ textTransform: 'none', fontWeight: 700 }}
            >
              Fechar
            </Button>
          </DialogActions>
        </Dialog>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 0.8,
            mb: 0.8,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 0.7,
            }}
          >
            <Tooltip
              title={`Clique no cabeçalho para ordenar. Use ${multiSortModifierLabel} + clique para ordenar por múltiplas colunas.`}
              arrow
            >
              <IconButton size='small' aria-label='Ajuda de ordenação' sx={{ color: 'text.secondary' }}>
                <Info size={14} />
              </IconButton>
            </Tooltip>
            {hasMultiSort ? (
              <Chip
                size='small'
                variant='outlined'
                label='Ordenação múltipla ativa'
                sx={{
                  fontWeight: 700,
                  color: appColors.actions.accentOutline.text,
                  borderColor: appColors.actions.accentOutline.border,
                  backgroundColor: 'transparent',
                }}
              />
            ) : null}
            {activeSortChips.map((chip) => (
              <Chip
                key={chip.field}
                size='small'
                label={chip.label}
                onDelete={() => removeSortColumn(chip.field)}
                sx={{ bgcolor: 'hsl(var(--muted) / 0.55)', fontWeight: 700 }}
              />
            ))}
          </Box>
          <Chip label={resultsOrderSummary} size='small' sx={{ bgcolor: 'hsl(var(--muted))', fontWeight: 700 }} />
        </Box>

        <Box>
          <GridComponent
            key={gridDataKey}
            cssClass='catalog-grid'
            dataSource={filteredInstruments}
            allowPaging
            allowSorting
            sortSettings={{ columns: sortColumns }}
            allowFiltering={false}
            allowResizing
            resizeSettings={{ mode: 'Auto' }}
            allowTextWrap={false}
            height='auto'
            rowHeight={40}
            enableStickyHeader
            ref={gridRef}
            pageSettings={{ pageSize: 10, pageSizes: [10, 25, 50, 75, 100] }}
            width='100%'
            actionComplete={(args: any) => {
              if (args?.requestType !== 'sorting' || !gridRef.current) return;
              const nextColumns = (gridRef.current.sortSettings?.columns ?? [])
                .map((item: any) => {
                  const field = String(item?.field ?? '').trim();
                  const direction = String(item?.direction ?? '').trim() === 'Descending' ? 'Descending' : 'Ascending';
                  if (!field) return null;
                  return { field, direction } as GridSortColumn;
                })
                .filter((item: GridSortColumn | null): item is GridSortColumn => item !== null);
              setSortColumns(nextColumns);
            }}
            actionBegin={(args: any) => {
              if (args?.requestType !== 'sorting') return;
              const field = String(args?.columnName ?? '').trim();
              const direction = String(args?.direction ?? '').trim() === 'Descending' ? 'Descending' : 'Ascending';
              if (!field) return;
              sortDirectionByFieldRef.current[field] = direction;
            }}
            dataBound={() => {
              applyGridHeaderTooltips();
              applyGridColumnVisibility();
            }}
            locale='pt'
          >
            <ColumnsDirective>
              {columns.map((column) => {
                const field = String(column.field ?? '').trim();
                if (!field) return null;
                return <ColumnDirective key={field} {...column} />;
              })}
            </ColumnsDirective>
            <Inject services={[Page, Sort, Filter, Resize]} />
          </GridComponent>
        </Box>
      </Paper>
    </Box>
  );
}
